// BUILD ARTIFACT - generated by tools/build_theme.py; never edit here.
// Bundle of engine/config-engine.js + ui/configurator-ui.js + ui/grouped-ui.js
// + ui/creator-ui.js + adapter/theme-adapter.js (single file so Shopify CDN asset versioning
// covers all of it - see build_theme.py).
/**
 * hbi-configurator logic engine.
 *
 * Pure functions over a config object loaded from a Shopify JSON metafield.
 * No DOM, no Shopify APIs, no dependencies - the theme layer wires this to
 * the page, and tests exercise it directly.
 *
 * Config format (schemaVersion 1):
 * {
 *   schemaVersion: 1,
 *   family: { key: "cms-bench", label: "CMS Rolling Bench" },
 *   options: [
 *     { key: "width", label: "Width", values: [{ key: "w48", label: "4 ft" }, ...] },
 *     ...
 *   ],
 *   matrix: [
 *     ["w48", "l96", "std", "abs", "none", "CMS-B-4896-STD-ABS-N"],
 *     ...
 *   ]
 * }
 *
 * Each matrix row has one value key per option (in options order) plus the
 * real NetSuite/Shopify SKU as the final element. The matrix is the single
 * source of truth: dependency rules are derived from it, never stored.
 *
 * A "selections" object maps option key -> selected value key, e.g.
 * { width: "w48", length: "l96" }. Options not yet chosen are absent.
 */

// Soft ceiling for serialized config size, in characters. Shopify's real
// hard limit for JSON metafields is 131,072 chars (apps created after
// 2026-04-01, ours included - confirmed 2026-07-22). This constant is set
// with headroom below that, since a family can still grow (more SKUs, a
// new option) after launch.
const CONFIG_SIZE_SOFT_LIMIT = 110000;

const SKU_COL_OFFSET = 1; // matrix row length = options.length + SKU_COL_OFFSET

function isNonEmptyString(v) {
  return typeof v === "string" && v.trim().length > 0;
}

/**
 * Structural validation. Returns { valid, errors, warnings }.
 * errors block use; warnings (unreachable values, size) need review but
 * don't stop the engine.
 *
 * Dispatches on schemaVersion: 1 is the original Configurator (below); 2 is
 * Grouped/Bundle (see validateGroupedConfig and the Grouped/Bundle section
 * further down this file). The two shapes are otherwise unrelated - a
 * schemaVersion 2 config never touches options/matrix/addOns/layoutPreview.
 */
function validateConfig(config) {
  if (!config || typeof config !== "object") {
    return { valid: false, errors: ["config is not an object"], warnings: [] };
  }
  if (config.schemaVersion === 2) {
    return validateGroupedConfig(config);
  }
  return validateConfiguratorConfig(config);
}

function validateConfiguratorConfig(config) {
  const errors = [];
  const warnings = [];

  if (config.schemaVersion !== 1) {
    errors.push(`unsupported schemaVersion: ${config.schemaVersion}`);
  }
  if (!config.family || !isNonEmptyString(config.family.key)) {
    errors.push("family.key is missing or empty");
  }

  const options = Array.isArray(config.options) ? config.options : null;
  if (!options || options.length === 0) {
    errors.push("options must be a non-empty array");
    return { valid: false, errors, warnings };
  }

  const optionKeys = new Set();
  for (const [i, opt] of options.entries()) {
    if (!isNonEmptyString(opt.key)) {
      errors.push(`options[${i}].key is missing or empty`);
      continue;
    }
    if (optionKeys.has(opt.key)) errors.push(`duplicate option key: ${opt.key}`);
    optionKeys.add(opt.key);
    if (!isNonEmptyString(opt.label)) {
      errors.push(`option "${opt.key}" has no label`);
    }
    const values = Array.isArray(opt.values) ? opt.values : [];
    if (values.length === 0) {
      errors.push(`option "${opt.key}" has no values`);
    }
    const valueKeys = new Set();
    for (const val of values) {
      if (!isNonEmptyString(val.key)) {
        errors.push(`option "${opt.key}" has a value with a missing key`);
        continue;
      }
      if (valueKeys.has(val.key)) {
        errors.push(`option "${opt.key}" has duplicate value key: ${val.key}`);
      }
      valueKeys.add(val.key);
    }
  }

  const matrix = Array.isArray(config.matrix) ? config.matrix : null;
  if (!matrix || matrix.length === 0) {
    errors.push("matrix must be a non-empty array");
    return { valid: false, errors, warnings };
  }

  const expectedLen = options.length + SKU_COL_OFFSET;
  const seenCombos = new Map();
  const usedValues = options.map(() => new Set());

  for (const [r, row] of matrix.entries()) {
    if (!Array.isArray(row) || row.length !== expectedLen) {
      errors.push(`matrix row ${r} has length ${row?.length}, expected ${expectedLen}`);
      continue;
    }
    let rowOk = true;
    for (let c = 0; c < options.length; c++) {
      const opt = options[c];
      const values = Array.isArray(opt.values) ? opt.values : [];
      if (!values.some((v) => v.key === row[c])) {
        errors.push(`matrix row ${r}: "${row[c]}" is not a value of option "${opt.key}"`);
        rowOk = false;
      }
    }
    const sku = row[expectedLen - 1];
    if (!isNonEmptyString(sku)) {
      errors.push(`matrix row ${r} has an empty SKU`);
      rowOk = false;
    }
    if (!rowOk) continue;

    const comboKey = row.slice(0, options.length).join(" ");
    if (seenCombos.has(comboKey)) {
      errors.push(
        `matrix row ${r} duplicates row ${seenCombos.get(comboKey)} (same combination, SKUs "${matrix[seenCombos.get(comboKey)][expectedLen - 1]}" and "${sku}")`
      );
    } else {
      seenCombos.set(comboKey, r);
    }
    for (let c = 0; c < options.length; c++) usedValues[c].add(row[c]);
  }

  // Values defined on an option but never used by any matrix row can never
  // be selected. Usually a sign the matrix is incomplete.
  for (const [c, opt] of options.entries()) {
    for (const val of opt.values ?? []) {
      if (isNonEmptyString(val.key) && !usedValues[c].has(val.key)) {
        warnings.push(`option "${opt.key}" value "${val.key}" never appears in the matrix`);
      }
    }
  }

  // handlePrefix is optional (real families need it so the theme adapter
  // can compute each SKU's product handle at render time; demo/mock
  // configs that never call live Shopify endpoints can omit it).
  if (config.handlePrefix !== undefined && typeof config.handlePrefix !== "string") {
    errors.push("handlePrefix must be a string");
  }

  // addOns is optional (Configurator Add-Ons, added 2026-08-21). Each entry
  // just references another wrapper's own config/matrix by handle - it
  // never duplicates that matrix here, so validation only checks that
  // matchOn points at option keys this config actually has.
  if (config.addOns !== undefined) {
    if (!Array.isArray(config.addOns)) {
      errors.push("addOns must be an array");
    } else {
      for (const [i, addOn] of config.addOns.entries()) {
        if (!addOn || typeof addOn !== "object") {
          errors.push(`addOns[${i}] is not an object`);
          continue;
        }
        if (!isNonEmptyString(addOn.familyKey)) {
          errors.push(`addOns[${i}].familyKey is missing or empty`);
        }
        if (!isNonEmptyString(addOn.wrapperHandle)) {
          errors.push(`addOns[${i}].wrapperHandle is missing or empty`);
        }
        const matchOn = Array.isArray(addOn.matchOn) ? addOn.matchOn : null;
        if (!matchOn || matchOn.length === 0) {
          errors.push(`addOns[${i}].matchOn must be a non-empty array`);
          continue;
        }
        for (const key of matchOn) {
          if (!optionKeys.has(key)) {
            errors.push(`addOns[${i}].matchOn references unknown option key: ${key}`);
          }
        }
        // requiresAddOn is optional (added 2026-10-01 for LSS, which mounts
        // to the PSS trellis frame and makes no sense without it). Points at
        // another addOns[] entry's wrapperHandle - checked loosely (string,
        // non-empty) since the UI is the one place that actually needs to
        // look it up against addOnChecked state at render time.
        if (addOn.requiresAddOn !== undefined && !isNonEmptyString(addOn.requiresAddOn)) {
          errors.push(`addOns[${i}].requiresAddOn must be a non-empty string when present`);
        }
      }
    }
  }

  // layoutPreview is optional (Bench Layout Preview, added 2026-08-24) - an
  // opt-in flag, same reasoning as addOns: only families where a top-view
  // footprint diagram actually makes sense (bench-shaped families with
  // "<number>ft" width/length option keys - see parseFeetKey) should turn
  // this on. Validated eagerly here (not deferred to the theme/UI layer) so
  // a family missing "width"/"length" options fails fast at config-save
  // time instead of silently rendering a broken diagram later.
  if (config.layoutPreview !== undefined) {
    if (typeof config.layoutPreview !== "boolean") {
      errors.push("layoutPreview must be a boolean");
    } else if (config.layoutPreview) {
      if (!optionKeys.has("width")) errors.push('layoutPreview requires a "width" option');
      if (!optionKeys.has("length")) errors.push('layoutPreview requires a "length" option');
    }
  }

  // requiredCompanion is optional (TD/TN floor track, added 2026-10-07) - a
  // MANDATORY paired product whose own SKU and quantity are derived, not
  // customer-chosen, unlike addOns (always optional, qty always 1). Only
  // TD/TN set this so far. Validated the same way as addOns/layoutPreview:
  // structural checks here, not deferred to the UI, since a malformed
  // qtyTable or skuFormat should fail config-save, not silently misquote a
  // customer at checkout time.
  if (config.requiredCompanion !== undefined) {
    const rc = config.requiredCompanion;
    if (!rc || typeof rc !== "object") {
      errors.push("requiredCompanion must be an object");
    } else {
      if (!isNonEmptyString(rc.label)) errors.push("requiredCompanion.label is missing or empty");
      if (!isNonEmptyString(rc.skuFormat) || !rc.skuFormat.includes("{ft}") || !rc.skuFormat.includes("{in}")) {
        errors.push('requiredCompanion.skuFormat must be a string containing both "{ft}" and "{in}"');
      }
      if (!optionKeys.has("width")) errors.push('requiredCompanion requires a "width" option');
      if (!optionKeys.has("length")) errors.push('requiredCompanion requires a "length" option');

      const KNOWN_FORMULAS = new Set(["roomWidthMinusInches"]);
      const lf = rc.lengthFormula;
      if (!lf || typeof lf !== "object" || !KNOWN_FORMULAS.has(lf.type)) {
        errors.push(`requiredCompanion.lengthFormula.type must be one of: ${[...KNOWN_FORMULAS].join(", ")}`);
      } else if (lf.type === "roomWidthMinusInches" && !(typeof lf.inches === "number" && lf.inches >= 0)) {
        errors.push("requiredCompanion.lengthFormula.inches must be a non-negative number");
      }

      const qtyTable = Array.isArray(rc.qtyTable) ? rc.qtyTable : null;
      if (!qtyTable || qtyTable.length === 0) {
        errors.push("requiredCompanion.qtyTable must be a non-empty array");
      } else {
        qtyTable.forEach((row, i) => {
          if (
            !Array.isArray(row) ||
            row.length !== 3 ||
            !row.every((n) => typeof n === "number") ||
            row[0] > row[1] ||
            !Number.isInteger(row[2]) ||
            row[2] <= 0
          ) {
            errors.push(`requiredCompanion.qtyTable[${i}] must be [min, max, qty] with min<=max and a positive integer qty`);
          }
        });
      }
    }
  }

  // Family switch (2026-10-08, first case: TD Drive <-> TN Neutral): links
  // between sibling wrapper pages that share one option structure, so a
  // customer can flip type without losing their size. Structure only - a
  // page handle and SKU prefix per option, never price/stock.
  if (config.familySwitch !== undefined) {
    const fs = config.familySwitch;
    if (!fs || typeof fs !== "object") {
      errors.push("familySwitch must be an object");
    } else {
      const opts = Array.isArray(fs.options) ? fs.options : null;
      if (!opts || opts.length < 2) {
        errors.push("familySwitch.options must list at least 2 pages");
      } else {
        const keys = new Set();
        opts.forEach((o, i) => {
          for (const f of ["key", "label", "wrapperHandle", "skuPrefix"]) {
            if (!o || !isNonEmptyString(o[f])) errors.push(`familySwitch.options[${i}].${f} is missing or empty`);
          }
          if (o && keys.has(o.key)) errors.push(`familySwitch has duplicate key: ${o.key}`);
          if (o) keys.add(o.key);
        });
        if (!keys.has(fs.current)) errors.push("familySwitch.current must match one of familySwitch.options[].key");
      }
    }
  }

  const size = JSON.stringify(config).length;
  if (size > CONFIG_SIZE_SOFT_LIMIT) {
    warnings.push(
      `serialized config is ${size} chars, over the ${CONFIG_SIZE_SOFT_LIMIT} soft limit - plan metafield splitting`
    );
  }

  return { valid: errors.length === 0, errors, warnings };
}

function optionIndex(config, optionKey) {
  const idx = config.options.findIndex((o) => o.key === optionKey);
  if (idx === -1) throw new Error(`unknown option key: ${optionKey}`);
  return idx;
}

/**
 * True if the matrix row is consistent with every selection, optionally
 * ignoring one option (used when computing that option's own availability,
 * so an already-made choice can still be changed).
 */
function rowMatches(config, row, selections, ignoreOptionKey = null) {
  for (let c = 0; c < config.options.length; c++) {
    const key = config.options[c].key;
    if (key === ignoreOptionKey) continue;
    const sel = selections[key];
    if (sel !== undefined && row[c] !== sel) return false;
  }
  return true;
}

/** Matrix rows consistent with the current selections. */
function matchingRows(config, selections) {
  return config.matrix.filter((row) => rowMatches(config, row, selections));
}

/**
 * Values for one option given the current selections, each flagged with
 * availability. A value is available if at least one matrix row is
 * consistent with it plus every OTHER current selection (the option's own
 * selection is ignored so the user can switch it).
 */
function optionValues(config, selections, optionKey) {
  const idx = optionIndex(config, optionKey);
  const candidateRows = config.matrix.filter((row) =>
    rowMatches(config, row, selections, optionKey)
  );
  const availableKeys = new Set(candidateRows.map((row) => row[idx]));
  return config.options[idx].values.map((v) => ({
    key: v.key,
    label: v.label,
    available: availableKeys.has(v.key),
  }));
}

/** True when every option has a selection. */
function isComplete(config, selections) {
  return config.options.every((o) => selections[o.key] !== undefined);
}

/** First option (in display order) without a selection, or null. */
function firstIncompleteOption(config, selections) {
  const opt = config.options.find((o) => selections[o.key] === undefined);
  return opt ? opt.key : null;
}

/**
 * Drop selections that are no longer reachable. Walks options in display
 * order, keeping each selection only if it's still available given the
 * selections kept so far. Call after any selection change: picking a new
 * width may invalidate a previously chosen length.
 */
function pruneSelections(config, selections) {
  const kept = {};
  for (const opt of config.options) {
    const sel = selections[opt.key];
    if (sel === undefined) continue;
    const values = optionValues(config, kept, opt.key);
    if (values.some((v) => v.key === sel && v.available)) kept[opt.key] = sel;
  }
  return kept;
}

/**
 * Resolve a complete, valid set of selections to its SKU.
 * Returns the SKU string, or null if selections are incomplete or match no
 * matrix row. On a validated config a complete selection matches at most
 * one row (duplicates are a validation error).
 */
function resolveSku(config, selections) {
  if (!isComplete(config, selections)) return null;
  const rows = matchingRows(config, selections);
  if (rows.length === 0) return null;
  return rows[0][config.options.length];
}

/**
 * For each add-on configured on this family, the selections to resolve
 * against THAT add-on's own matrix (fetched and resolved separately by the
 * caller - this function never sees the add-on's config, only its
 * matchOn key list). `selections` is null while any matched option is
 * still unpicked here, so the caller can render a placeholder card rather
 * than treating it as "no compatible add-on found".
 *
 * Matching is purely by shared option-key name (e.g. both this family and
 * the add-on's own config having a "width" option) - never a hand-
 * maintained pairwise table, per the matrix-is-the-source-of-truth rule.
 */
function addOnMatches(config, selections) {
  return (config.addOns || []).map((addOn) => {
    const matched = {};
    for (const key of addOn.matchOn) {
      const val = selections[key];
      if (val === undefined) return { ...addOn, selections: null };
      matched[key] = val;
    }
    return { ...addOn, selections: matched };
  });
}

/**
 * An add-on's own option keys that the host family's selections never cover
 * (e.g. LSS's "height" - nothing about a bench's own width/length/height/
 * tray determines a light-support post height, so the customer has to pick
 * it on the add-on card itself). Added 2026-10-01 alongside LSS, the first
 * add-on with more dimensions than its matchOn list. Pure and config-only -
 * the caller merges whatever the customer picks for these keys into
 * addOnMatches' selections before calling resolveSku against the add-on's
 * own config.
 */
function addOnOwnOptionKeys(addOnConfig, matchOn) {
  const matched = new Set(matchOn);
  return (addOnConfig.options || [])
    .map((opt) => opt.key)
    .filter((key) => !matched.has(key));
}

/**
 * Inverse of resolveSku: the selections that produce a given SKU, or null
 * if the SKU isn't in the matrix. Used to deep-link the configurator from
 * a real SKU's own product page (?sku=... on the wrapper URL) straight to
 * that configuration - no "make your selections" state on landing.
 */
function selectionsForSku(config, sku) {
  const row = config.matrix.find((r) => r[config.options.length] === sku);
  if (!row) return null;
  const selections = {};
  config.options.forEach((opt, i) => (selections[opt.key] = row[i]));
  return selections;
}

/**
 * Bench Layout Preview support: option value keys for width/length follow a
 * strict "<number>ft" convention across every family gen_family_config.py
 * has produced so far (e.g. "4ft", "4.5ft", "120ft" - confirmed against the
 * real ST/SR/MR/PSS data on disk, never "4'6\"" or similar). Returns the
 * numeric feet value, or null if the key doesn't match - callers should
 * treat null as "can't compute a footprint for this value" rather than
 * guessing, since layoutPreview is only meant to be turned on for families
 * where this convention actually holds (validateConfig doesn't check the
 * convention itself, only that width/length options exist).
 */
function parseFeetKey(key) {
  if (typeof key !== "string") return null;
  const m = /^(\d+(?:\.\d+)?)ft$/.exec(key);
  return m ? parseFloat(m[1]) : null;
}

/* ------------------------------------------------------------------ *
 * Required companion (TD/TN floor track), added 2026-10-07. CM's own
 * mechanics: a track bench's floor track is REQUIRED (never optional, so
 * this isn't shaped like addOns), its SKU length is derived from the
 * customer's room width (not one of the bench's own matrix options), and
 * its quantity is derived from the bench's own length via a vendor-sourced
 * lookup table - NOT from how many identical benches the customer orders
 * (confirmed with Dustin: benches of the same length share one rail run,
 * so companionQty below takes no bench-count argument at all - there is
 * nothing for a caller to multiply by).
 * ------------------------------------------------------------------ */

/**
 * Derived companion length in inches from the customer's room width, per
 * config.requiredCompanion.lengthFormula. Dispatches on formula `type` so a
 * future family's different offset doesn't need a new hardcoded branch
 * elsewhere - only one type exists today (CM's confirmed "room width minus
 * a fixed inches" rule). Returns null when requiredCompanion is absent or
 * roomWidthFt isn't a positive number, rather than guessing.
 */
function companionLengthInches(config, roomWidthFt) {
  const rc = config.requiredCompanion;
  if (!rc || typeof roomWidthFt !== "number" || !(roomWidthFt > 0)) return null;
  const lf = rc.lengthFormula;
  if (lf?.type === "roomWidthMinusInches") {
    const inches = Math.round(roomWidthFt * 12) - lf.inches;
    return inches > 0 ? inches : null;
  }
  return null;
}

/**
 * Derived companion quantity from the HOST bench's own selected length (its
 * OAL), via config.requiredCompanion.qtyTable - a vendor-sourced [min, max,
 * qty] lookup (CM's "Leg/Track Qty" table), never a hand-maintained rule.
 * Returns null if requiredCompanion is absent, length isn't selected yet,
 * or the length falls outside every row (surfaced as "confirm with CM"
 * by the caller, not silently guessed - same honesty rule the verification
 * prototype used for its own unconfirmed edge cases).
 */
function companionQty(config, selections) {
  const rc = config.requiredCompanion;
  if (!rc) return null;
  const lengthFt = parseFeetKey(selections.length);
  if (lengthFt === null) return null;
  const row = rc.qtyTable.find(([min, max]) => lengthFt >= min && lengthFt <= max);
  return row ? row[2] : null;
}

/**
 * Link to a sibling page in config.familySwitch (e.g. TD -> TN), carrying the
 * customer's current build over: `currentSku` is re-prefixed for the target
 * (TD4Fx16F20PT -> TN4Fx16F20PT) and sent as ?sku=, and `roomWidthFt` as
 * ?room=. The target page silently ignores a SKU it doesn't sell (e.g. TN
 * stops at 30 ft) - see selectionsForSku's deep-link handling - so this
 * never has to know the other page's matrix. Null for an unknown target or
 * a config without a switch.
 */
function familySwitchHref(config, targetKey, currentSku, roomWidthFt) {
  const fs = config.familySwitch;
  if (!fs || !Array.isArray(fs.options)) return null;
  const target = fs.options.find((o) => o.key === targetKey);
  const current = fs.options.find((o) => o.key === fs.current);
  if (!target) return null;
  const params = ["view=configurator"];
  if (isNonEmptyString(currentSku) && current && currentSku.startsWith(current.skuPrefix)) {
    params.push("sku=" + encodeURIComponent(target.skuPrefix + currentSku.slice(current.skuPrefix.length)));
  }
  if (typeof roomWidthFt === "number" && roomWidthFt > 0) params.push("room=" + roomWidthFt);
  return `/products/${target.wrapperHandle}?${params.join("&")}`;
}

/**
 * Formats the companion's real SKU from a derived length in inches, per
 * config.requiredCompanion.skuFormat (e.g. "FTB{ft}F{in}" -> "FTB11F9" for
 * 141in, "FTB10F" for 120in) - confirmed against real live FTB product
 * handles, not a guessed convention.
 */
function companionSku(config, lengthInches) {
  const rc = config.requiredCompanion;
  if (!rc || typeof lengthInches !== "number") return null;
  const ft = Math.floor(lengthInches / 12);
  const inch = lengthInches % 12;
  // Whole-foot lengths drop the inch digit entirely - the real SKU is
  // "FTB10F", never "FTB10F0" (confirmed against the 2026-10-07 product
  // export: all 120 whole-foot FTB SKUs end in "F").
  return rc.skuFormat.replace("{ft}", String(ft)).replace("{in}", inch === 0 ? "" : String(inch));
}

/* ------------------------------------------------------------------ *
 * Grouped/Bundle (schemaVersion 2), added 2026-09-04. First case:
 * Dosatron NDS. See docs/status-2026-09-04-grouped-bundle-plan.md and
 * schema/config.schema.json for the full design record.
 *
 * Config format (schemaVersion 2):
 * {
 *   schemaVersion: 2,
 *   family: { key: "dosatron-nds", label: "Dosatron Nutrient Delivery System" },
 *   minSelected: 1,                          // optional, default 1
 *   series: [                                // optional - omit for a
 *     { key: "lo-flo", label: "Lo-Flo", spec: "...", description: "..." },
 *     { key: "hi-flo", label: "Hi-Flo", spec: "...", description: "..." },
 *   ],                                       // bundle with no meta-selector
 *   sections: [
 *     {
 *       key: "dosers", label: "Dosers", description: "...", required: false,
 *       // items is an object keyed by series key when "series" is set above,
 *       // or a flat array when it's omitted - never both in the same config.
 *       items: {
 *         "lo-flo": [{ sku: "D14MZ2VFBPHY", handle: "dosatron-...", name: "..." }],
 *         "hi-flo": [{ sku: "D40MZ2BPVFHY", handle: "dosatron-...", name: "..." }],
 *       },
 *       helpPrompt: { trigger: "...", title: "...", body: "..." },  // optional
 *       autoSelect: false,  // optional, default false - see autoSelectedDefaults()
 *     },
 *   ],
 *   relatedArticles: [{ title: "...", url: "https://learn.hydrobuilder.com/..." }],
 * }
 *
 * A "selected" object here maps SKU -> quantity (a positive integer; a SKU
 * absent or mapped to 0 is not selected), NOT option key -> value key - a
 * genuinely different selection shape from the Configurator's "selections",
 * since there's no single resolved SKU to converge on. Price and inventory
 * are never stored or computed here either - same hard constraint as the
 * Configurator, enforced by never taking a price argument in any function
 * below. The theme adapter multiplies live per-SKU price by quantity itself.
 */

function isPlainObject(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function isNonNegativeInt(v) {
  return typeof v === "number" && Number.isInteger(v) && v >= 0;
}

function validateTags(tags, where, errors) {
  if (tags === undefined) return;
  if (!Array.isArray(tags) || !tags.every(isNonEmptyString)) {
    errors.push(`${where}.tags must be an array of non-empty strings`);
  }
}

function validateGroupedItem(item, where, errors) {
  if (!item || typeof item !== "object") {
    errors.push(`${where} is not an object`);
    return;
  }
  if (isOptionGroup(item)) {
    validateOptionGroup(item, where, errors);
    return;
  }
  if (!isNonEmptyString(item.sku)) errors.push(`${where}.sku is missing or empty`);
  if (!isNonEmptyString(item.handle)) errors.push(`${where}.handle is missing or empty`);
  if (!isNonEmptyString(item.name)) errors.push(`${where}.name is missing or empty`);
  if (item.key !== undefined && !isNonEmptyString(item.key)) errors.push(`${where}.key must be a non-empty string when present`);
  validateTags(item.tags, where, errors);
  validatePack(item, where, errors);
}

// Optional "pack" (units per purchase) + "unit" (singular noun) - only used
// for the full-width Creator page's per-unit price line (2026-10-09).
function validatePack(obj, where, errors) {
  if (obj.pack !== undefined && !(Number.isInteger(obj.pack) && obj.pack > 0)) errors.push(`${where}.pack must be a positive integer`);
  if (obj.unit !== undefined && !isNonEmptyString(obj.unit)) errors.push(`${where}.unit must be a non-empty string`);
}

/*
 * Option groups (added 2026-10-09, first case: Netafim Drip System Creator).
 * One row in a section that condenses many similar products into chip
 * pickers - e.g. a drip stake assembly sold in 5 flow rates x 5 tube lengths
 * is one row with two pickers instead of 22 rows. Same faceted idea as the
 * Configurator's option matrix (availability is derived from the variant
 * list, never a separate rules table - CLAUDE.md hard constraint 4), scoped
 * to a single row:
 *   { key, name, spec?, description?, tags?,
 *     options: [{ key, label, values: [{ key, label, swatch? }] }],
 *     variants: [{ sku, handle, values: { <optionKey>: <valueKey> }, spec?, tags? }],
 *     default?: { <optionKey>: <valueKey> } }
 * Each variant is a real product (or a real variant of one) - selection
 * state stays sku -> qty, so totals, required-section checks and Add to
 * Cart treat a picked variant exactly like a plain item.
 */
function isOptionGroup(item) {
  return !!item && Array.isArray(item.options) && Array.isArray(item.variants);
}

function validateOptionGroup(group, where, errors) {
  if (!isNonEmptyString(group.key)) errors.push(`${where}.key is required for an option group`);
  if (!isNonEmptyString(group.name)) errors.push(`${where}.name is missing or empty`);
  validateTags(group.tags, where, errors);
  validatePack(group, where, errors);
  if (group.options.length === 0) errors.push(`${where}.options must be a non-empty array`);
  if (group.variants.length === 0) errors.push(`${where}.variants must be a non-empty array`);

  const valueKeys = new Map(); // optionKey -> Set(valueKey)
  group.options.forEach((opt, i) => {
    if (!opt || !isNonEmptyString(opt.key)) {
      errors.push(`${where}.options[${i}].key is missing or empty`);
      return;
    }
    if (valueKeys.has(opt.key)) errors.push(`${where} has duplicate option key: ${opt.key}`);
    if (!isNonEmptyString(opt.label)) errors.push(`${where}.options "${opt.key}" has no label`);
    const set = new Set();
    if (!Array.isArray(opt.values) || opt.values.length === 0) {
      errors.push(`${where}.options "${opt.key}".values must be a non-empty array`);
    } else {
      opt.values.forEach((v, j) => {
        if (!v || !isNonEmptyString(v.key) || !isNonEmptyString(v.label)) {
          errors.push(`${where}.options "${opt.key}".values[${j}] needs a non-empty key and label`);
        } else if (set.has(v.key)) {
          errors.push(`${where}.options "${opt.key}" has duplicate value key: ${v.key}`);
        } else {
          set.add(v.key);
        }
      });
    }
    valueKeys.set(opt.key, set);
  });

  function checkValues(values, at) {
    if (!isPlainObject(values)) {
      errors.push(`${at}.values must be an object of optionKey -> valueKey`);
      return false;
    }
    let ok = true;
    for (const [optKey, set] of valueKeys) {
      if (!set.has(values[optKey])) {
        errors.push(`${at}.values.${optKey} must be one of this group's "${optKey}" value keys`);
        ok = false;
      }
    }
    for (const k of Object.keys(values)) {
      if (!valueKeys.has(k)) {
        errors.push(`${at}.values has unknown option key: ${k}`);
        ok = false;
      }
    }
    return ok;
  }

  const combos = new Set();
  const skus = new Set();
  group.variants.forEach((v, i) => {
    const at = `${where}.variants[${i}]`;
    if (!v || typeof v !== "object") {
      errors.push(`${at} is not an object`);
      return;
    }
    if (!isNonEmptyString(v.sku)) errors.push(`${at}.sku is missing or empty`);
    if (!isNonEmptyString(v.handle)) errors.push(`${at}.handle is missing or empty`);
    validateTags(v.tags, at, errors);
    validatePack(v, at, errors);
    if (isNonEmptyString(v.sku)) {
      if (skus.has(v.sku)) errors.push(`${where} lists SKU "${v.sku}" in more than one variant`);
      skus.add(v.sku);
    }
    if (checkValues(v.values, at)) {
      const combo = group.options.map((o) => v.values[o.key]).join("|");
      if (combos.has(combo)) errors.push(`${at} repeats an option combination another variant already uses`);
      combos.add(combo);
    }
  });

  if (group.default !== undefined && checkValues(group.default, `${where}.default`)) {
    if (!groupVariant(group, group.default)) errors.push(`${where}.default doesn't match any variant`);
  }
}

/** The variant matching every option in `picks`, or null. */
function groupVariant(group, picks) {
  if (!picks) return null;
  return group.variants.find((v) => group.options.every((o) => v.values[o.key] === picks[o.key])) || null;
}

/**
 * Faceted availability for one chip: is there any variant with
 * optionKey=valueKey that also matches every OTHER current pick? Same rule
 * the Configurator's optionValues() applies to the bench matrix.
 */
function groupValueAvailable(group, picks, optionKey, valueKey) {
  return group.variants.some(
    (v) =>
      v.values[optionKey] === valueKey &&
      group.options.every((o) => o.key === optionKey || !picks || v.values[o.key] === picks[o.key])
  );
}

/** Starting picks: the configured default, else the first variant's values. */
function groupDefaultPicks(group) {
  const base = group.default && groupVariant(group, group.default) ? group.default : group.variants[0]?.values;
  return { ...(base || {}) };
}

/**
 * After the customer changes `changedKey`, keep that choice and move the
 * other picks to the closest real variant (most other picks kept, earliest
 * listed wins a tie) - so picking a flow rate a tube length doesn't come in
 * never strands the row on "not available". Returns new picks.
 */
function groupRepick(group, picks, changedKey) {
  if (groupVariant(group, picks)) return { ...picks };
  let best = null;
  let bestScore = -1;
  for (const v of group.variants) {
    if (v.values[changedKey] !== picks[changedKey]) continue;
    const score = group.options.filter((o) => v.values[o.key] === picks[o.key]).length;
    if (score > bestScore) {
      best = v;
      bestScore = score;
    }
  }
  return best ? { ...best.values } : groupDefaultPicks(group);
}

/** Human label for a variant's option values, e.g. "0.5 GPH · 24 in". */
function groupVariantLabel(group, variant) {
  return group.options
    .map((o) => (o.values.find((v) => v.key === variant.values[o.key]) || {}).label)
    .filter(Boolean)
    .join(" · ");
}

/**
 * Every concrete, purchasable item in a section's item list: plain items as
 * they are, each option-group variant as its own item (named after the
 * group plus its picked values, tags merged from group + variant, and
 * carrying groupKey so the UI can find its row again).
 */
function expandItems(items) {
  const out = [];
  for (const item of items || []) {
    if (!isOptionGroup(item)) {
      out.push(item);
      continue;
    }
    for (const v of item.variants) {
      out.push({
        sku: v.sku,
        handle: v.handle,
        name: `${item.name} (${groupVariantLabel(item, v)})`,
        spec: v.spec ?? item.spec,
        tags: [...(item.tags || []), ...(v.tags || [])],
        groupKey: item.key,
        values: v.values,
        ...(v.pack ?? item.pack ? { pack: v.pack ?? item.pack } : {}),
        ...(item.unit ? { unit: item.unit } : {}),
      });
    }
  }
  return out;
}

/*
 * Tips (added 2026-10-09): data-driven pairing suggestions - never
 * requirements (Dustin: "not sure we would want to make anything required,
 * but we should give suggestions and pairing tips"). A tip shows when the
 * tags of the currently selected items satisfy its `when`:
 *   { when: { all?: [tag], any?: [tag], none?: [tag] },
 *     level: "tip" | "warn" | "ok", text, section?: <section key> }
 * `section` places it inside that section (and the summary lists every
 * active non-"ok" tip); without it, it shows in the summary only. Text may
 * use **bold**.
 */
const TIP_LEVELS = new Set(["tip", "warn", "ok"]);

function validateTips(config, sectionKeys, errors) {
  if (config.tips === undefined) return;
  if (!Array.isArray(config.tips)) {
    errors.push("tips must be an array");
    return;
  }
  config.tips.forEach((t, i) => {
    const at = `tips[${i}]`;
    if (!t || typeof t !== "object") {
      errors.push(`${at} is not an object`);
      return;
    }
    if (!TIP_LEVELS.has(t.level)) errors.push(`${at}.level must be "tip", "warn" or "ok"`);
    if (!isNonEmptyString(t.text)) errors.push(`${at}.text is missing or empty`);
    if (t.section !== undefined && !sectionKeys.has(t.section)) errors.push(`${at}.section references unknown section: ${t.section}`);
    const w = t.when;
    if (!isPlainObject(w)) {
      errors.push(`${at}.when must be an object with all/any/none tag lists`);
      return;
    }
    let lists = 0;
    for (const k of ["all", "any", "none"]) {
      if (w[k] === undefined) continue;
      lists++;
      if (!Array.isArray(w[k]) || w[k].length === 0 || !w[k].every(isNonEmptyString)) {
        errors.push(`${at}.when.${k} must be a non-empty array of tags`);
      }
    }
    if (!lists) errors.push(`${at}.when needs at least one of all/any/none`);
    for (const k of Object.keys(w)) {
      if (!["all", "any", "none"].includes(k)) errors.push(`${at}.when has unknown key: ${k}`);
    }
  });
}

/*
 * Optional `page` block (2026-10-09) - layout for the full-width Creator
 * page (ui/creator-ui.js): { hero?: {...}, flow?: { steps: [{ label, sub?,
 * sections?, external? }] }, tabs?: { paths: [{ key, label, sections }] } }.
 * Only the cross-references are checked here (section keys must exist, a
 * section may sit in one tab path only); copy fields are free text.
 */
function validatePage(config, sectionKeys, errors) {
  const page = config.page;
  if (page === undefined) return;
  if (!isPlainObject(page)) {
    errors.push("page must be an object");
    return;
  }
  const steps = page.flow && page.flow.steps;
  if (page.flow !== undefined && !Array.isArray(steps)) errors.push("page.flow.steps must be an array");
  (Array.isArray(steps) ? steps : []).forEach((st, i) => {
    if (!st || !isNonEmptyString(st.label)) errors.push(`page.flow.steps[${i}].label is missing or empty`);
    for (const k of (st && st.sections) || []) if (!sectionKeys.has(k)) errors.push(`page.flow.steps[${i}] references unknown section: ${k}`);
  });
  const paths = page.tabs && page.tabs.paths;
  if (page.tabs !== undefined && !Array.isArray(paths)) errors.push("page.tabs.paths must be an array");
  const seen = new Map();
  const pathKeys = new Set();
  (Array.isArray(paths) ? paths : []).forEach((p, i) => {
    if (!p || !isNonEmptyString(p.key) || !isNonEmptyString(p.label)) errors.push(`page.tabs.paths[${i}] needs a non-empty key and label`);
    else if (pathKeys.has(p.key)) errors.push(`page.tabs.paths has duplicate key: ${p.key}`);
    else pathKeys.add(p.key);
    if (!p || !Array.isArray(p.sections) || !p.sections.length) {
      errors.push(`page.tabs.paths[${i}].sections must be a non-empty array`);
      return;
    }
    for (const k of p.sections) {
      if (!sectionKeys.has(k)) errors.push(`page.tabs.paths[${i}] references unknown section: ${k}`);
      else if (seen.has(k)) errors.push(`section "${k}" is in more than one tab path`);
      else seen.set(k, i);
    }
  });
}

/*
 * Optional `discounts` (2026-10-09): build-savings tiers SHOWN on the
 * full-width Creator page - { tiers: [{ min: <dollars>, percent }], note? }.
 * Display only: the real discount is a Shopify automatic discount with the
 * same numbers, so the two must be kept in sync by whoever changes either.
 */
function validateDiscounts(config, errors) {
  const d = config.discounts;
  if (d === undefined) return;
  if (!isPlainObject(d) || !Array.isArray(d.tiers) || d.tiers.length === 0) {
    errors.push("discounts must be an object with a non-empty tiers array");
    return;
  }
  let prevMin = 0;
  let prevPct = 0;
  d.tiers.forEach((t, i) => {
    if (!t || typeof t.min !== "number" || !(t.min > 0)) errors.push(`discounts.tiers[${i}].min must be a positive number (dollars)`);
    if (!t || typeof t.percent !== "number" || !(t.percent > 0 && t.percent < 100)) errors.push(`discounts.tiers[${i}].percent must be between 0 and 100`);
    if (t && t.min <= prevMin) errors.push(`discounts.tiers must be in increasing order of min (tier ${i})`);
    if (t && t.percent <= prevPct) errors.push(`discounts.tiers percent must increase with min (tier ${i})`);
    if (t) { prevMin = t.min; prevPct = t.percent; }
  });
  if (d.note !== undefined && !isNonEmptyString(d.note)) errors.push("discounts.note must be a non-empty string when present");
}

/** Set of tags carried by every selected (qty > 0) item. */
function selectedTags(config, seriesKey, selected) {
  const tags = new Set();
  for (const row of selectedItems(config, seriesKey, selected)) {
    for (const t of row.item.tags || []) tags.add(t);
  }
  return tags;
}

/** Tips whose `when` matches the current selection, in config order. */
function activeTips(config, seriesKey, selected) {
  if (!Array.isArray(config.tips)) return [];
  const tags = selectedTags(config, seriesKey, selected);
  return config.tips.filter((t) => {
    const w = t.when || {};
    if (w.all && !w.all.every((x) => tags.has(x))) return false;
    if (w.any && !w.any.some((x) => tags.has(x))) return false;
    if (w.none && w.none.some((x) => tags.has(x))) return false;
    return true;
  });
}

function validateGroupedConfig(config) {
  const errors = [];
  const warnings = [];

  if (config.schemaVersion !== 2) {
    errors.push(`unsupported schemaVersion: ${config.schemaVersion}`);
  }
  if (!config.family || !isNonEmptyString(config.family.key)) {
    errors.push("family.key is missing or empty");
  }

  if (config.minSelected !== undefined && !isNonNegativeInt(config.minSelected)) {
    errors.push("minSelected must be a non-negative integer");
  }

  const seriesKeys = new Set();
  const hasSeriesField = config.series !== undefined;
  if (hasSeriesField) {
    const series = Array.isArray(config.series) ? config.series : null;
    if (!series || series.length === 0) {
      errors.push("series, when present, must be a non-empty array");
    } else {
      for (const [i, s] of series.entries()) {
        if (!s || !isNonEmptyString(s.key)) {
          errors.push(`series[${i}].key is missing or empty`);
          continue;
        }
        if (seriesKeys.has(s.key)) errors.push(`duplicate series key: ${s.key}`);
        seriesKeys.add(s.key);
        if (!isNonEmptyString(s.label)) errors.push(`series "${s.key}" has no label`);
      }
    }
  }

  const sections = Array.isArray(config.sections) ? config.sections : null;
  if (!sections || sections.length === 0) {
    errors.push("sections must be a non-empty array");
    return { valid: false, errors, warnings };
  }

  const sectionKeys = new Set();
  const skuOwners = new Map(); // sku -> "section.series" it first appeared under, for duplicate detection
  // Item keys (option groups always, plain items optionally) are how a
  // quick-start block or a share link points at a row, so they must be
  // unique across the whole config. Only checked for flat (no-series)
  // configs - under series the same row legitimately repeats per series.
  const itemKeys = new Map();
  function noteGroupKey(item, sectionKey) {
    if (!item || !isNonEmptyString(item.key)) return;
    if (itemKeys.has(item.key)) errors.push(`item key "${item.key}" is used more than once (sections "${itemKeys.get(item.key)}" and "${sectionKey}")`);
    else itemKeys.set(item.key, sectionKey);
  }

  for (const [i, section] of sections.entries()) {
    if (!section || !isNonEmptyString(section.key)) {
      errors.push(`sections[${i}].key is missing or empty`);
      continue;
    }
    if (sectionKeys.has(section.key)) errors.push(`duplicate section key: ${section.key}`);
    sectionKeys.add(section.key);
    if (!isNonEmptyString(section.label)) errors.push(`section "${section.key}" has no label`);
    if (section.required !== undefined && typeof section.required !== "boolean") {
      errors.push(`section "${section.key}".required must be a boolean`);
    }
    if (section.autoSelect !== undefined && typeof section.autoSelect !== "boolean") {
      errors.push(`section "${section.key}".autoSelect must be a boolean`);
    }
    if (section.helpPrompt !== undefined) {
      const hp = section.helpPrompt;
      if (!hp || typeof hp !== "object" || !isNonEmptyString(hp.trigger) || !isNonEmptyString(hp.title) || !isNonEmptyString(hp.body)) {
        errors.push(`section "${section.key}".helpPrompt must have non-empty trigger/title/body`);
      }
    }

    const items = section.items;
    if (Array.isArray(items)) {
      if (hasSeriesField && seriesKeys.size > 0) {
        errors.push(`section "${section.key}".items is a flat array, but this config defines "series" - it must be keyed by series instead`);
      }
      items.forEach((item, j) => {
        validateGroupedItem(item, `section "${section.key}".items[${j}]`, errors);
        noteGroupKey(item, section.key);
        for (const concrete of isOptionGroup(item) ? expandItems([item]) : [item]) {
          if (!isNonEmptyString(concrete?.sku)) continue;
          const ownerKey = `${concrete.sku}`;
          if (skuOwners.has(ownerKey) && skuOwners.get(ownerKey) !== section.key) {
            warnings.push(`SKU "${concrete.sku}" appears in more than one section ("${skuOwners.get(ownerKey)}" and "${section.key}")`);
          }
          skuOwners.set(ownerKey, section.key);
        }
      });
    } else if (isPlainObject(items)) {
      if (!hasSeriesField || seriesKeys.size === 0) {
        errors.push(`section "${section.key}".items is keyed by series, but this config defines no "series" - it must be a flat array instead`);
      } else {
        for (const key of Object.keys(items)) {
          if (!seriesKeys.has(key)) {
            errors.push(`section "${section.key}".items references unknown series key: ${key}`);
          }
        }
        for (const seriesKey of seriesKeys) {
          const list = items[seriesKey];
          if (list === undefined) {
            warnings.push(`section "${section.key}" has no items for series "${seriesKey}"`);
            continue;
          }
          if (!Array.isArray(list)) {
            errors.push(`section "${section.key}".items["${seriesKey}"] must be an array`);
            continue;
          }
          list.forEach((item, j) => {
            validateGroupedItem(item, `section "${section.key}".items["${seriesKey}"][${j}]`, errors);
            for (const concrete of isOptionGroup(item) ? expandItems([item]) : [item]) {
              if (!isNonEmptyString(concrete?.sku)) continue;
              const ownerKey = `${seriesKey}:${concrete.sku}`;
              if (skuOwners.has(ownerKey) && skuOwners.get(ownerKey) !== section.key) {
                warnings.push(`SKU "${concrete.sku}" (series "${seriesKey}") appears in more than one section ("${skuOwners.get(ownerKey)}" and "${section.key}")`);
              }
              skuOwners.set(ownerKey, section.key);
            }
          });
        }
      }
    } else {
      errors.push(`section "${section.key}".items must be an array or an object keyed by series`);
    }
  }

  validateTips(config, sectionKeys, errors);
  validatePage(config, sectionKeys, errors);
  validateDiscounts(config, errors);

  if (config.relatedArticles !== undefined) {
    if (!Array.isArray(config.relatedArticles)) {
      errors.push("relatedArticles must be an array");
    } else {
      config.relatedArticles.forEach((a, i) => {
        if (!a || !isNonEmptyString(a.title) || !isNonEmptyString(a.url)) {
          errors.push(`relatedArticles[${i}] must have non-empty title and url`);
        }
      });
    }
  }

  const size = JSON.stringify(config).length;
  if (size > CONFIG_SIZE_SOFT_LIMIT) {
    warnings.push(
      `serialized config is ${size} chars, over the ${CONFIG_SIZE_SOFT_LIMIT} soft limit - plan metafield splitting`
    );
  }

  return { valid: errors.length === 0, errors, warnings };
}

/** True when the config has a Series meta-selector step at all. */
function hasSeries(config) {
  return Array.isArray(config.series) && config.series.length > 0;
}

/**
 * Resolved sections for the given series (or for a config with no series at
 * all - pass null/undefined as seriesKey, section.items must then already be
 * a flat array per validateGroupedConfig). Each returned section carries its
 * already-filtered `items` list ready to render, plus its own required flag
 * and optional helpPrompt.
 */
function sectionsForSeries(config, seriesKey) {
  return (config.sections || []).map((section) => {
    let items;
    if (Array.isArray(section.items)) {
      items = section.items;
    } else if (isPlainObject(section.items)) {
      items = (seriesKey != null && section.items[seriesKey]) || [];
    } else {
      items = [];
    }
    return {
      key: section.key,
      label: section.label,
      description: section.description ?? "",
      required: !!section.required,
      autoSelect: !!section.autoSelect,
      helpPrompt: section.helpPrompt ?? null,
      items,
      concreteItems: expandItems(items),
    };
  });
}

/**
 * A `selected` map (sku -> 1) for every `autoSelect` section that resolves to
 * exactly one item for this series - e.g. a "Controller" section with a
 * single per-series unit, so picking the series doesn't also require a
 * separate click to add the one item that choice already implies. A section
 * with zero or more than one resolved item is left alone (nothing to
 * unambiguously default to). Called fresh on every series change - never
 * merges with prior selections, matching the existing reset-on-series-change
 * behavior for everything else.
 */
function autoSelectedDefaults(config, seriesKey) {
  const selected = {};
  for (const section of sectionsForSeries(config, seriesKey)) {
    if (section.autoSelect && section.concreteItems.length === 1) {
      selected[section.concreteItems[0].sku] = 1;
    }
  }
  return selected;
}

/**
 * Every currently-selected item across all visible sections for this series,
 * from a `selected` map of sku -> quantity (absent or 0 = not selected).
 * Never touches price - callers read that live per sku.
 */
function selectedItems(config, seriesKey, selected) {
  const out = [];
  for (const section of sectionsForSeries(config, seriesKey)) {
    for (const item of section.concreteItems) {
      const qty = selected?.[item.sku] || 0;
      if (qty > 0) out.push({ sectionKey: section.key, item, qty });
    }
  }
  return out;
}

/** { distinctCount, totalQty } across every selected item. */
function selectionTotals(config, seriesKey, selected) {
  const rows = selectedItems(config, seriesKey, selected);
  return {
    distinctCount: rows.length,
    totalQty: rows.reduce((sum, r) => sum + r.qty, 0),
  };
}

/** Keys of sections flagged `required` that have nothing selected yet. */
function unmetRequiredSections(config, seriesKey, selected) {
  return sectionsForSeries(config, seriesKey)
    .filter((s) => s.required)
    .filter((s) => !s.concreteItems.some((item) => (selected?.[item.sku] || 0) > 0))
    .map((s) => s.key);
}

/**
 * Whether the current state can add to cart: a series must be picked first
 * if the config has one, the config's minSelected total must be met (default
 * 1 - Dosatron's confirmed rule), and every `required` section must have at
 * least one selection.
 */
function canAddToCart(config, seriesKey, selected) {
  if (hasSeries(config) && seriesKey == null) return false;
  const minSelected = isNonNegativeInt(config.minSelected) ? config.minSelected : 1;
  const { totalQty } = selectionTotals(config, seriesKey, selected);
  if (totalQty < minSelected) return false;
  if (unmetRequiredSections(config, seriesKey, selected).length > 0) return false;
  return true;
}

/**
 * Configurator UI - environment-agnostic rendering over the logic engine.
 *
 * Knows nothing about Shopify. The host page supplies an adapter:
 *   getVariant(sku, config) -> Promise<{ price, available, variantId } | null>
 *       price in cents; config is passed through so the adapter can read
 *       config.handlePrefix (or whatever else it needs) to locate the SKU
 *   addToCart(variantId, quantity, sku) -> Promise<{ ok, error? }>
 *       sku is the resolved SKU being added - adapters that don't need it
 *       (the demo/mock ones) can just ignore the extra argument
 *
 * The theme layer (theme/assets/hbi-configurator.js) passes an adapter
 * backed by the product .js endpoint and /cart/add.js; the demo passes
 * fakes. Same UI code runs in both, so the demo actually demos the
 * production behavior.
 *
 * Buy-button stance (2026-07-14, Dustin): Add to cart is ALWAYS visible,
 * like a standard PDP - never hidden behind selection progress. While
 * selections are incomplete it renders as visually disabled but stays
 * clickable; clicking it highlights the unselected options and focuses
 * the first one, instead of doing nothing.
 *
 * Failure stance: anything unexpected (invalid config, variant lookup
 * miss, fetch error) degrades to the quote fallback link - never a
 * broken or misleading buy button.
 *
 * Deep-link support (2026-07-24): opts.initialSku pre-fills selections to
 * whatever configuration produces that SKU, so a redirect from that SKU's
 * own product page lands showing its exact price/options immediately
 * rather than an empty picker - see VC-155.
 *
 * Configurator Add-Ons (2026-08-24): config.addOns (optional, see
 * engine/config-engine.js addOnMatches) drives a card per add-on, below
 * the option selections and above the qty/Add to Cart controls. Three
 * states, same fail-safe philosophy as the primary buy flow:
 *   - pending: the add-on's matchOn options aren't all picked yet.
 *   - call-in: matchOn options are picked but no matrix row on the
 *     add-on's OWN config matches (e.g. PSS has no half-foot widths) -
 *     never a dead "not available", always a way to still get it.
 *   - ready: a real SKU resolved on the add-on's own matrix; live price/
 *     stock/image fetched via adapter.getVariant(sku, addOnConfig) - the
 *     SAME function used for the primary product, just pointed at a
 *     different config so handlePrefix resolves against that family.
 * opts.addOnConfigs supplies each add-on's own config (options + matrix),
 * keyed by wrapperHandle - never fetched live, embedded by Liquid same as
 * the primary config (see hbi-configurator-buybox.liquid). A checked,
 * resolved, in-stock add-on rides in the SAME /cart/add.js call as the
 * primary item (adapter.addToCart's extraItems param) - one click, one
 * atomic add, matching the Selleasy pattern this was modeled on.
 *
 * Visual refresh (2026-08-24, approved by the team): option fields with a
 * small number of values (<= PILL_MAX_VALUES, e.g. Width, Height, Tray)
 * render as a pill/radio group instead of a native <select>. Options with
 * more values than that (e.g. Length, ~117 values on some families) stay
 * a <select> - pills don't scale to that many - but restyled to match
 * (custom chevron, brand colors) rather than left as a bare browser
 * dropdown. The choice is per-option and automatic (based on
 * optionValues(...).length), not hardcoded to specific option keys, so it
 * applies the same way across every family without per-family code.
 * Pills are real <input type="radio"> elements (visually hidden, native
 * behavior kept) grouped by a per-instance name, not custom-built
 * buttons - full keyboard/screen-reader support for free, no bespoke ARIA
 * needed. Deliberately drops the "(unavailable)" text suffix the old
 * <select> used for a disabled option - the native disabled state plus
 * the dimmed/struck-through pill style already convey it, and the extra
 * text crowded a pill's small footprint. The <select> branch keeps that
 * suffix unchanged.
 *
 * Required companion (2026-10-07, TD/TN floor track): config.requiredCompanion
 * (optional, see engine/config-engine.js companionLengthInches/companionQty/
 * companionSku) renders one MANDATORY card, visually similar to an add-on
 * card but with no checkbox - it's not something the customer opts into. It
 * needs one new input this UI didn't have before (room width - not one of
 * the bench's own matrix options), and its resolved quantity is independent
 * of the bench quantity stepper below (confirmed with Dustin: benches of the
 * same length share one floor-track run, so ordering 3 benches still needs
 * the SAME track quantity as ordering 1 - see companionQty's own doc
 * comment). "Add to cart" is gated on this resolving + being in stock
 * exactly the same way it's gated on the primary bench, via
 * recomputeAddReady() - a track bench can't be added without its required
 * track, same failure stance as everything else in this file (never a
 * broken or misleading buy button).
 *
 * Bench Layout Preview (2026-08-24): when config.layoutPreview is true,
 * this dispatches a bubbling "hbi:selectionchange" CustomEvent on `root`
 * every time selections/quantity/add-on-checked change, with detail
 * { widthFt, lengthFt, quantity, addOnActive } (widthFt/lengthFt are
 * numbers or null via parseFeetKey - null means "not selected yet or not
 * a parseable value", never a guess). The diagram itself lives in a
 * SEPARATE Liquid block below the image gallery (see
 * hbi-bench-diagram.liquid) - a different part of the page than this buy
 * box, so it can't just read local state here. It listens for this event
 * on `document` instead of importing anything from this module - the two
 * stay decoupled on purpose.
 */

// 237 -> "19 ft 9 in", 240 -> "20 ft" (whole inches only - track lengths
// are whole inches); matches the "16 ft" style of the option labels.
function formatFtIn(totalInches) {
  const ft = Math.floor(totalInches / 12);
  const inch = totalInches % 12;
  return inch ? `${ft} ft ${inch} in` : `${ft} ft`;
}

function formatMoney(cents) {
  if (typeof cents !== "number" || !isFinite(cents)) return "";
  const dollars = Math.floor(cents / 100);
  const rem = String(cents % 100).padStart(2, "0");
  return `$${dollars.toLocaleString("en-US")}.${rem}`;
}

const PLACEHOLDER = "placeholder";
const PILL_MAX_VALUES = 8;

let instanceCounter = 0;

function createConfigurator(root, config, adapter, opts = {}) {
  const fallbackUrl = opts.fallbackUrl || "/pages/contact";
  const fallbackLabel = opts.fallbackLabel || "Request a quote";
  const doc = root.ownerDocument;
  const instanceId = ++instanceCounter; // namespaces radio group names, see renderFields

  function el(tag, className, text) {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function renderFallback(message) {
    root.textContent = "";
    const box = el("div", "hbi-cfg__fallback");
    box.appendChild(el("p", "hbi-cfg__fallback-msg", message));
    const link = el("a", "hbi-cfg__fallback-link", fallbackLabel);
    link.href = fallbackUrl;
    box.appendChild(link);
    root.appendChild(box);
  }

  const check = validateConfig(config);
  if (!check.valid) {
    // config is broken - never render a half-working form
    renderFallback("This product's configurator is temporarily unavailable.");
    return { state: () => null, destroy: () => (root.textContent = "") };
  }

  // Deep-link support: opts.initialSku (from the wrapper URL's ?sku=) lands
  // the customer directly on that configuration - no re-picking, no visible
  // "make your selections" state. Silently ignored if the SKU isn't in the
  // matrix (config changed, stale link) - falls through to the normal
  // empty-selections start instead of erroring.
  let selections = (opts.initialSku && selectionsForSku(config, opts.initialSku)) || {};
  let quantity = 1;
  let resolved = null; // { sku, variantId, variant } while a SKU is resolved
  let busy = false;
  let requestSeq = 0; // stale-response guard for getVariant
  let showMissing = false; // highlight unselected options (after early add click)

  // Configurator Add-Ons state. Keyed by wrapperHandle so it survives
  // re-renders (the add-on defs themselves come from static config, never
  // change mid-session). addOnChecked persists a customer's opt-in choice
  // even while addOnResolved is being refetched after a selection change.
  const addOnConfigs = opts.addOnConfigs || {};
  const addOnChecked = {};
  const addOnResolved = {}; // wrapperHandle -> { sku, variant, label } | undefined
  // Own-option picks for add-ons that have a dimension no bench selection
  // covers (e.g. LSS's post height - added 2026-10-01). wrapperHandle ->
  // { [optionKey]: value }. Persists across re-renders same as addOnChecked.
  const addOnSubSelections = {};
  let addOnSeq = 0; // stale-response guard, same pattern as requestSeq

  // Required companion (TD/TN floor track) state. roomWidthFt is the one
  // input this family needs that isn't a matrix option. companionResolved
  // mirrors addOnResolved's shape plus its own derived `qty` (never the
  // bench quantity stepper's value - see companionQty's doc comment).
  // opts.initialRoomWidthFt: carried over from a sibling page's switch
  // link (?room=), so a customer flipping TD <-> TN doesn't retype it.
  let roomWidthFt =
    config.requiredCompanion && typeof opts.initialRoomWidthFt === "number" && opts.initialRoomWidthFt > 0
      ? opts.initialRoomWidthFt
      : null;
  let companionResolved = null; // { sku, variant, qty } | null
  let companionSeq = 0; // stale-response guard, same pattern as requestSeq/addOnSeq

  const form = el("div", "hbi-cfg");
  const fields = el("div", "hbi-cfg__fields");
  const result = el("div", "hbi-cfg__result");
  const addOnsEl = el("div", "hbi-cfg__addons");
  const companionEl = el("div", "hbi-cfg__companion");
  // The room-width input is built ONCE and kept; only the card below it is
  // redrawn per update. Rebuilding the input on every keystroke (the
  // 2026-10-07 version) replaced the focused element mid-typing, so a
  // customer could only ever enter one character at a time.
  const companionInputSlot = el("div", "hbi-cfg__companion-input");
  const companionCardSlot = el("div", "hbi-cfg__companion-card");
  companionEl.appendChild(companionInputSlot);
  companionEl.appendChild(companionCardSlot);
  let roomWidthInputBuilt = false;
  const addOnNote = el("p", "hbi-cfg__addon-note");
  addOnNote.hidden = true;

  function svgIcon(build) {
    const svg = doc.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    svg.setAttribute("class", "hbi-cfg__icon");
    build(svg);
    return svg;
  }

  function cartIcon() {
    return svgIcon((svg) => {
      for (const [cx, cy] of [[9, 20], [18, 20]]) {
        const wheel = doc.createElementNS("http://www.w3.org/2000/svg", "circle");
        wheel.setAttribute("cx", cx);
        wheel.setAttribute("cy", cy);
        wheel.setAttribute("r", "1.4");
        wheel.setAttribute("fill", "currentColor");
        wheel.setAttribute("stroke", "none");
        svg.appendChild(wheel);
      }
      const body = doc.createElementNS("http://www.w3.org/2000/svg", "path");
      body.setAttribute("d", "M2.5 3.5h2.6l2.1 12.1a1.8 1.8 0 0 0 1.8 1.5h8.6a1.8 1.8 0 0 0 1.77-1.47L21 8H6.4");
      svg.appendChild(body);
    });
  }

  function setAddBtnContent(text, withIcon) {
    addBtn.textContent = "";
    if (withIcon) addBtn.appendChild(cartIcon());
    addBtn.appendChild(doc.createTextNode(text));
  }

  // Persistent buy controls - present from first render, like a normal PDP.
  const controls = el("div", "hbi-cfg__controls");
  const qtyStepper = el("span", "hbi-cfg__qty-stepper");
  const qtyDec = el("button", "hbi-cfg__qty-btn", "−");
  qtyDec.type = "button";
  qtyDec.setAttribute("aria-label", "Decrease quantity");
  const qty = el("input", "hbi-cfg__qty");
  qty.type = "number";
  qty.min = "1";
  qty.value = String(quantity);
  const qtyInc = el("button", "hbi-cfg__qty-btn", "+");
  qtyInc.type = "button";
  qtyInc.setAttribute("aria-label", "Increase quantity");

  function setQuantity(next) {
    quantity = Math.max(1, next);
    qty.value = String(quantity);
    emitLayoutPreviewEvent();
  }
  qty.addEventListener("change", () => setQuantity(parseInt(qty.value, 10) || 1));
  qtyDec.addEventListener("click", () => setQuantity(quantity - 1));
  qtyInc.addEventListener("click", () => setQuantity(quantity + 1));
  qtyStepper.appendChild(qtyDec);
  qtyStepper.appendChild(qty);
  qtyStepper.appendChild(qtyInc);

  const addBtn = el("button", "hbi-cfg__add");
  addBtn.type = "button";
  setAddBtnContent("Add to cart", true);
  addBtn.addEventListener("click", onAddClick);
  controls.appendChild(qtyStepper);
  controls.appendChild(addBtn);

  if (config.familySwitch) form.appendChild(renderFamilySwitch());
  form.appendChild(fields);
  form.appendChild(result);
  form.appendChild(companionEl);
  form.appendChild(addOnsEl);
  form.appendChild(addOnNote);
  form.appendChild(controls);
  root.textContent = "";
  root.appendChild(form);

  function setAddReady(ready) {
    // Visually disabled but still clickable when not ready, so an early
    // click can highlight what's missing (a truly disabled button is inert).
    addBtn.setAttribute("aria-disabled", ready ? "false" : "true");
  }

  // The combined buy-gate: the primary bench resolved+in-stock, AND (only
  // for families with a required companion) that companion also resolved+
  // in-stock. Two independent async flows (updateResult/updateRequiredCompanion)
  // can each finish after the other, so both call this rather than setting
  // setAddReady(true) directly - whichever finishes last re-evaluates the
  // combined state correctly instead of racing.
  function recomputeAddReady() {
    if (!resolved || !resolved.variant.available) {
      setAddReady(false);
      return;
    }
    if (config.requiredCompanion && (!companionResolved || !companionResolved.variant.available)) {
      setAddReady(false);
      return;
    }
    setAddReady(true);
  }

  // Shared by both the pill and <select> branches below - one selection
  // change always triggers this exact sequence, regardless of which
  // control produced it.
  function applySelection(key, value) {
    if (value === PLACEHOLDER) delete selections[key];
    else selections[key] = value;
    selections = pruneSelections(config, selections);
    if (isComplete(config, selections)) showMissing = false;
    renderFields();
    void updateResult();
    void updateAddOns();
    void updateRequiredCompanion();
    emitLayoutPreviewEvent();
  }

  function renderPillGroup(wrap, opt, values, labelId) {
    const group = el("div", "hbi-cfg__pills");
    group.setAttribute("role", "radiogroup");
    group.setAttribute("aria-labelledby", labelId);
    const name = `hbi-cfg-${instanceId}-${opt.key}`;

    for (const v of values) {
      const pill = el("label", "hbi-cfg__pill");
      const input = doc.createElement("input");
      input.type = "radio";
      input.name = name;
      input.value = v.key;
      input.className = "hbi-cfg__pill-input";
      input.disabled = !v.available;
      input.checked = selections[opt.key] === v.key;
      input.addEventListener("change", () => applySelection(opt.key, v.key));
      pill.appendChild(input);
      pill.appendChild(el("span", "hbi-cfg__pill-label", v.label));
      group.appendChild(pill);
    }
    wrap.appendChild(group);
  }

  function renderSelect(wrap, opt, values, labelId) {
    const selectWrap = el("div", "hbi-cfg__select-wrap");
    const select = el("select", "hbi-cfg__select");
    select.dataset.option = opt.key;
    select.setAttribute("aria-labelledby", labelId);

    const ph = el("option", "", `Select ${opt.label}...`);
    ph.value = PLACEHOLDER;
    select.appendChild(ph);

    for (const v of values) {
      const o = el("option", "", v.available ? v.label : `${v.label} (unavailable)`);
      o.value = v.key;
      o.disabled = !v.available;
      if (selections[opt.key] === v.key) o.selected = true;
      select.appendChild(o);
    }
    select.addEventListener("change", () => applySelection(opt.key, select.value));
    selectWrap.appendChild(select);
    wrap.appendChild(selectWrap);
  }

  function renderFields() {
    fields.textContent = "";
    for (const opt of config.options) {
      const wrap = el("div", "hbi-cfg__field");
      if (showMissing && selections[opt.key] === undefined) {
        wrap.classList.add("hbi-cfg__field--missing");
      }
      const labelId = `hbi-cfg-label-${instanceId}-${opt.key}`;
      const labelEl = el("span", "hbi-cfg__label", opt.label);
      labelEl.id = labelId;
      wrap.appendChild(labelEl);

      const values = optionValues(config, selections, opt.key);
      if (values.length <= PILL_MAX_VALUES) {
        renderPillGroup(wrap, opt, values, labelId);
      } else {
        renderSelect(wrap, opt, values, labelId);
      }
      fields.appendChild(wrap);
    }
  }

  async function updateResult() {
    resolved = null;
    controls.hidden = false;
    if (!isComplete(config, selections)) {
      setAddReady(false);
      result.textContent = "";
      const remaining = config.options.filter((o) => selections[o.key] === undefined).length;
      result.appendChild(
        el("p", "hbi-cfg__hint",
          `${remaining} selection${remaining === 1 ? "" : "s"} to go`)
      );
      if (showMissing) {
        result.appendChild(
          el("p", "hbi-cfg__error", "Please choose the highlighted options.")
        );
      }
      return;
    }

    const sku = resolveSku(config, selections);
    if (!sku) {
      // valid-looking selections with no cartable target: quote path
      renderResultFallback();
      return;
    }

    const seq = ++requestSeq;
    setAddReady(false);
    result.textContent = "";
    result.appendChild(el("p", "hbi-cfg__hint", "Checking price and availability..."));

    let variant = null;
    try {
      variant = await adapter.getVariant(sku, config);
    } catch {
      variant = null;
    }
    if (seq !== requestSeq) return; // user changed selections mid-flight

    if (!variant) {
      renderResultFallback();
      return;
    }
    resolved = { sku, variantId: variant.variantId, variant };
    renderResultBuy();
  }

  function renderResultFallback() {
    setAddReady(false);
    controls.hidden = true; // quote link replaces purchase in this state
    result.textContent = "";
    const box = el("div", "hbi-cfg__fallback");
    box.appendChild(
      el("p", "hbi-cfg__fallback-msg",
        "This configuration can't be ordered online right now.")
    );
    const link = el("a", "hbi-cfg__fallback-link", fallbackLabel);
    link.href = fallbackUrl;
    box.appendChild(link);
    result.appendChild(box);
  }

  function renderResultBuy() {
    const { sku, variant } = resolved;
    result.textContent = "";

    const row = el("div", "hbi-cfg__buyrow");
    row.appendChild(el("span", "hbi-cfg__price", formatMoney(variant.price)));
    row.appendChild(el("span", "hbi-cfg__sku", sku));
    result.appendChild(row);

    if (!variant.available) {
      setAddReady(false);
      result.appendChild(el("p", "hbi-cfg__oos", "Out of stock"));
      const link = el("a", "hbi-cfg__fallback-link", fallbackLabel);
      link.href = fallbackUrl;
      result.appendChild(link);
      return;
    }

    controls.hidden = false;
    recomputeAddReady();
  }

  // Configurator Add-Ons: renders every configured add-on's card, one of
  // three states each (pending / call-in / ready) - see the module doc
  // comment above. Sequential, not parallel, per add-on: today there's
  // realistically one add-on per family, and parallelizing adds
  // complexity (result ordering) for no visible benefit yet.
  async function updateAddOns() {
    const seq = ++addOnSeq;
    addOnsEl.textContent = "";
    const matches = addOnMatches(config, selections);

    for (const match of matches) {
      const card = el("div", "hbi-cfg__addon");
      addOnsEl.appendChild(card);

      if (match.selections === null) {
        // A previously resolved add-on can go stale the moment selections
        // change again (e.g. switching width away from a matched one) -
        // clear it so a checked-but-no-longer-resolved add-on can't leave
        // its price note stranded on screen or ride along into the cart.
        delete addOnResolved[match.wrapperHandle];
        card.classList.add("hbi-cfg__addon--pending");
        card.appendChild(
          el("p", "hbi-cfg__addon-hint", "Finish your selections above to see a matching add-on.")
        );
        continue;
      }

      const addOnConfig = addOnConfigs[match.wrapperHandle];
      if (!addOnConfig) {
        delete addOnResolved[match.wrapperHandle]; // not embedded - never show a broken card
        card.remove(); // already appended above - leaving it drew an empty bordered box
        continue;
      }

      // Gating (added 2026-10-01 for LSS, which mounts to the PSS trellis
      // frame and makes no sense without it): render a locked card and skip
      // resolution entirely until the required add-on is checked.
      if (match.requiresAddOn && !addOnChecked[match.requiresAddOn]) {
        delete addOnResolved[match.wrapperHandle];
        delete addOnSubSelections[match.wrapperHandle];
        renderAddOnLocked(card, addOnConfig, addOnConfigs[match.requiresAddOn]);
        continue;
      }

      // Own options (added 2026-10-01): a dimension on the add-on's own
      // config that no bench selection determines (e.g. LSS's post height).
      // Rendered first so it sits above whatever state follows it in the
      // same card - loading/call-in/ready, or the "pick this" hint below.
      const ownKeys = addOnOwnOptionKeys(addOnConfig, match.matchOn);
      const subSelections = addOnSubSelections[match.wrapperHandle] || {};
      const fullSelections = { ...match.selections, ...subSelections };

      if (ownKeys.length > 0) {
        // Keeps the card compact/inline (title, own-option picker, price,
        // checkbox all in one row, like a plain add-on with no own options -
        // e.g. PSS) instead of the picker stranding itself on its own line
        // with dead space while its price/checkbox haven't resolved yet.
        card.classList.add("hbi-cfg__addon--own-inline");
        renderAddOnOwnOptions(card, addOnConfig, ownKeys, subSelections, (key, value) => {
          addOnSubSelections[match.wrapperHandle] = {
            ...(addOnSubSelections[match.wrapperHandle] || {}),
            [key]: value,
          };
          updateAddOns(); // re-render, same as any other selection change
        });
      }

      const missingOwnKeys = ownKeys.filter((k) => fullSelections[k] === undefined);
      if (missingOwnKeys.length > 0) {
        delete addOnResolved[match.wrapperHandle];
        card.appendChild(
          el("p", "hbi-cfg__addon-hint", "Choose the option above to see price and add this.")
        );
        continue;
      }

      const addOnSku = resolveSku(addOnConfig, fullSelections);
      if (!addOnSku) {
        delete addOnResolved[match.wrapperHandle];
        renderAddOnCallIn(card, addOnConfig);
        continue;
      }

      const loadingNote = el("p", "hbi-cfg__addon-hint", "Checking availability...");
      card.classList.add("hbi-cfg__addon--loading");
      card.appendChild(loadingNote);

      let variant = null;
      try {
        variant = await adapter.getVariant(addOnSku, addOnConfig);
      } catch {
        variant = null;
      }
      if (seq !== addOnSeq) return; // selections changed again mid-flight

      card.classList.remove("hbi-cfg__addon--loading");
      loadingNote.remove();

      if (!variant) {
        delete addOnResolved[match.wrapperHandle];
        renderAddOnCallIn(card, addOnConfig);
        continue;
      }

      addOnResolved[match.wrapperHandle] = {
        sku: addOnSku,
        variant,
        label: addOnConfig.family?.label || match.familyKey,
      };
      renderAddOnReady(card, match.wrapperHandle, addOnConfig, variant, ownKeys.length > 0);
    }

    updateAddOnNote();
    emitLayoutPreviewEvent();
  }

  // Family switch (TD Drive <-> TN Neutral, 2026-10-08): a pill row at the
  // top of the buy box linking to each sibling page. The current page is a
  // plain marked pill; the others are real links whose href is recomputed
  // right before use (hover/focus/click) so it carries the size picked so
  // far - only once it's a complete SKU - plus the room width.
  function renderFamilySwitch() {
    const fs = config.familySwitch;
    // A highlighted panel, not just another option row - the other type
    // lives on a different page, so customers need to notice it exists.
    const field = el("div", "hbi-cfg__switch");
    const labelId = `hbi-cfg-label-${instanceId}-switch`;
    const labelEl = el("span", "hbi-cfg__switch-title", fs.label || "Type");
    labelEl.id = labelId;
    field.appendChild(labelEl);
    const row = el("div", "hbi-cfg__switch-row");
    row.setAttribute("role", "group");
    row.setAttribute("aria-labelledby", labelId);
    const currentHref = (key) =>
      familySwitchHref(config, key, isComplete(config, selections) ? resolveSku(config, selections) : null, roomWidthFt);
    for (const o of fs.options) {
      if (o.key === fs.current) {
        const cur = el("span", "hbi-cfg__switch-pill hbi-cfg__switch-pill--current");
        cur.setAttribute("aria-current", "page");
        cur.appendChild(el("strong", "hbi-cfg__switch-name", o.label));
        if (o.description) cur.appendChild(el("span", "hbi-cfg__switch-desc", o.description));
        cur.appendChild(el("span", "hbi-cfg__switch-sub", "\u2713 Viewing now"));
        row.appendChild(cur);
        continue;
      }
      const a = el("a", "hbi-cfg__switch-pill");
      a.appendChild(el("strong", "hbi-cfg__switch-name", o.label));
      // optional per-option blurb from the config (merchandiser-editable in
      // the Config Editor - wording isn't code)
      if (o.description) a.appendChild(el("span", "hbi-cfg__switch-desc", o.description));
      a.appendChild(el("span", "hbi-cfg__switch-sub", "Switch \u2192"));
      a.href = familySwitchHref(config, o.key, null, null);
      const refresh = () => { a.href = currentHref(o.key); };
      a.addEventListener("pointerenter", refresh);
      a.addEventListener("focus", refresh);
      a.addEventListener("click", refresh); // runs before the browser follows the link
      row.appendChild(a);
    }
    field.appendChild(row);
    field.appendChild(el("p", "hbi-cfg__switch-hint",
      fs.hint || "Same sizes on both \u2014 switching keeps your selections where that size is available."));
    return field;
  }

  // Room width input - the one piece of information a required companion
  // needs that isn't one of the bench's own matrix options. Built once into
  // companionInputSlot (see its declaration for why it must never be
  // rebuilt while the customer is typing).
  function renderRoomWidthInput(wrap) {
    const roomField = el("div", "hbi-cfg__field hbi-cfg__field--room-width");
    const labelId = `hbi-cfg-label-${instanceId}-roomwidth`;
    const labelEl = el("span", "hbi-cfg__label", config.requiredCompanion.roomWidthInput?.label || "Room width");
    labelEl.id = labelId;
    roomField.appendChild(labelEl);

    const inputWrap = el("div", "hbi-cfg__room-width-wrap");
    const input = doc.createElement("input");
    // Text + decimal keypad, not type="number": a number input reports ""
    // mid-entry for a value like "20." (so "20.5" can't be typed reliably)
    // and changes value on mouse-wheel scroll over the page.
    input.type = "text";
    input.inputMode = "decimal";
    input.autocomplete = "off";
    input.placeholder = "e.g. 20";
    input.min = String(config.requiredCompanion.roomWidthInput?.min ?? 0);
    input.step = "0.5";
    input.className = "hbi-cfg__room-width-input";
    input.setAttribute("aria-labelledby", labelId);
    if (roomWidthFt !== null) input.value = String(roomWidthFt);
    input.addEventListener("input", () => {
      // keep digits and one decimal point; "20.5 ft" or "20,5" still read sensibly
      const cleaned = input.value.replace(",", ".").replace(/[^0-9.]/g, "");
      const v = parseFloat(cleaned);
      roomWidthFt = Number.isFinite(v) && v > 0 ? v : null;
      void updateRequiredCompanion();
      emitLayoutPreviewEvent();
    });
    inputWrap.appendChild(input);
    inputWrap.appendChild(el("span", "hbi-cfg__room-width-unit", config.requiredCompanion.roomWidthInput?.unit || "ft"));
    roomField.appendChild(inputWrap);
    wrap.appendChild(roomField);
  }

  // Mirrors updateAddOns' resolve flow (pending / call-in / ready) for the
  // ONE required companion, but with no checkbox (it's mandatory, not
  // opt-in) and its own quantity from companionQty - never the bench
  // quantity stepper. No-op for every family without requiredCompanion.
  async function updateRequiredCompanion() {
    if (!config.requiredCompanion) return;
    const seq = ++companionSeq;
    if (!roomWidthInputBuilt) {
      renderRoomWidthInput(companionInputSlot);
      roomWidthInputBuilt = true;
    }
    companionCardSlot.textContent = "";

    const card = el("div", "hbi-cfg__addon hbi-cfg__addon--required");
    companionCardSlot.appendChild(card);
    card.appendChild(el("span", "hbi-cfg__addon-kicker", "Required"));
    card.appendChild(el("span", "hbi-cfg__addon-name", config.requiredCompanion.label));

    const lengthInches = companionLengthInches(config, roomWidthFt);
    const qty = companionQty(config, selections);

    if (lengthInches === null || qty === null) {
      companionResolved = null;
      card.classList.add("hbi-cfg__addon--pending");
      card.appendChild(
        el("p", "hbi-cfg__addon-hint",
          lengthInches === null
            ? "Enter your room width above to see your required floor track."
            : selections.length === undefined
              ? "Pick a bench length above to see how many floor-track pieces you need."
              : "This bench length isn't in our floor-track quantity table yet - we'll confirm the right quantity with CM Horticulture before this ships."
        )
      );
      recomputeAddReady();
      return;
    }

    const sku = companionSku(config, lengthInches);
    const loadingNote = el("p", "hbi-cfg__addon-hint", "Checking availability...");
    card.classList.add("hbi-cfg__addon--loading");
    card.appendChild(loadingNote);

    let variant = null;
    try {
      variant = await adapter.getVariant(sku, { handlePrefix: config.requiredCompanion.handlePrefix });
    } catch {
      variant = null;
    }
    if (seq !== companionSeq) return; // selections/room width changed again mid-flight

    card.classList.remove("hbi-cfg__addon--loading");
    loadingNote.remove();

    if (!variant) {
      companionResolved = null;
      card.classList.add("hbi-cfg__addon--callin");
      card.appendChild(
        el("p", "hbi-cfg__addon-hint",
          `This exact track length isn't orderable online yet, but we can special-order the ${qty}x your bench needs.`)
      );
      const link = el("a", "hbi-cfg__addon-call",
        opts.addOnCallPhone ? `Call ${opts.addOnCallPhone} to order` : fallbackLabel);
      link.href = opts.addOnCallPhone ? `tel:${opts.addOnCallPhone.replace(/[^\d+]/g, "")}` : fallbackUrl;
      card.appendChild(link);
      recomputeAddReady();
      return;
    }

    if (variant.image) {
      const thumb = el("div", "hbi-cfg__addon-thumb");
      const img = doc.createElement("img");
      img.src = variant.image;
      img.alt = "";
      thumb.appendChild(img);
      card.appendChild(thumb);
    }
    card.appendChild(el("span", "hbi-cfg__addon-detail", `${qty} pieces × ${formatFtIn(lengthInches)} each (room width − ${config.requiredCompanion.lengthFormula?.inches ?? 0} in)`));
    card.appendChild(el("span", "hbi-cfg__addon-price", `${qty} × ${formatMoney(variant.price)}`));

    if (!variant.available) {
      companionResolved = null;
      card.classList.add("hbi-cfg__addon--oos");
      card.appendChild(el("p", "hbi-cfg__addon-hint", "Currently out of stock."));
      recomputeAddReady();
      return;
    }

    companionResolved = { sku, variant, qty };
    card.classList.add("hbi-cfg__addon--ready");
    recomputeAddReady();
  }

  // Shown instead of attempting resolution at all while the add-on this one
  // requires (e.g. LSS needs PSS) isn't checked yet. requiredConfig may be
  // undefined (the required add-on isn't embedded on this page) - falls back
  // to a generic phrase rather than a broken label.
  function renderAddOnLocked(card, addOnConfig, requiredConfig) {
    card.classList.add("hbi-cfg__addon--locked");
    const requiredLabel = requiredConfig?.family?.label || "the add-on above";
    card.appendChild(el("span", "hbi-cfg__addon-kicker", "Requires " + requiredLabel));
    card.appendChild(el("span", "hbi-cfg__addon-name", addOnConfig.family?.label || ""));
    card.appendChild(
      el("p", "hbi-cfg__addon-requires", `Requires ${requiredLabel} to be added`)
    );
  }

  // Renders a picker for each of the add-on's own option keys (today, always
  // exactly one in practice - LSS's height), condensed to one row per key:
  // label + <select>, no silent default (a blank placeholder is always the
  // selected option first, so a customer can't miss picking the one they
  // actually want - see LSS's post-height mockup, 2026-10-01).
  function renderAddOnOwnOptions(card, addOnConfig, ownKeys, subSelections, onChange) {
    // Wrapped in .hbi-cfg__addon-body (not appended bare) so the title gets
    // the same flex: 1 1 auto treatment as every other add-on's title - the
    // own-option row/price/checkbox stay their natural compact width next to
    // it, instead of the title getting squeezed into a narrow wrapped column.
    const body = el("div", "hbi-cfg__addon-body");
    body.appendChild(el("span", "hbi-cfg__addon-kicker", "Compatible add-on"));
    body.appendChild(el("span", "hbi-cfg__addon-name", addOnConfig.family?.label || ""));
    card.appendChild(body);

    for (const key of ownKeys) {
      const opt = (addOnConfig.options || []).find((o) => o.key === key);
      if (!opt) continue;
      const row = el("div", "hbi-cfg__addon-own-row");
      row.appendChild(el("span", "hbi-cfg__addon-own-label", opt.label || key));

      const select = doc.createElement("select");
      select.className = "hbi-cfg__addon-own-select";
      const current = subSelections[key];
      if (current === undefined) select.classList.add("hbi-cfg__addon-own-select--missing");

      const placeholder = doc.createElement("option");
      placeholder.value = "";
      placeholder.textContent = `Choose ${(opt.label || key).toLowerCase()}…`;
      select.appendChild(placeholder);

      for (const val of opt.values || []) {
        const o = doc.createElement("option");
        o.value = val.key;
        o.textContent = val.label || val.key;
        if (val.key === current) o.selected = true;
        select.appendChild(o);
      }
      if (current === undefined) placeholder.selected = true;

      select.addEventListener("change", () => {
        if (select.value) onChange(key, select.value);
      });
      row.appendChild(select);
      card.appendChild(row);
    }
  }

  function renderAddOnCallIn(card, addOnConfig) {
    card.classList.add("hbi-cfg__addon--callin");
    const label = addOnConfig.family?.label || "this add-on";
    card.appendChild(el("span", "hbi-cfg__addon-kicker", "We can still get you one"));
    card.appendChild(
      el("p", "hbi-cfg__addon-hint",
        `This configuration doesn't have a ${label} SKU online yet, but we can special-order one.`)
    );
    // opts.addOnCallPhone (e.g. "(888) 815-9763") when set; otherwise the
    // same quote fallback already used elsewhere - never a dead end either way.
    const link = el("a", "hbi-cfg__addon-call",
      opts.addOnCallPhone ? `Call ${opts.addOnCallPhone} to order` : fallbackLabel);
    link.href = opts.addOnCallPhone ? `tel:${opts.addOnCallPhone.replace(/[^\d+]/g, "")}` : fallbackUrl;
    card.appendChild(link);
  }

  // skipHeader (added 2026-10-01): true when renderAddOnOwnOptions already
  // rendered this card's kicker+name above its own-option picker (e.g. LSS's
  // height select) - avoids rendering the name twice on the same card.
  function renderAddOnReady(card, wrapperHandle, addOnConfig, variant, skipHeader) {
    card.classList.add("hbi-cfg__addon--ready");

    if (variant.image) {
      const thumb = el("div", "hbi-cfg__addon-thumb");
      const img = doc.createElement("img");
      img.src = variant.image;
      img.alt = "";
      thumb.appendChild(img);
      card.appendChild(thumb);
    }

    if (!skipHeader) {
      const body = el("div", "hbi-cfg__addon-body");
      body.appendChild(el("span", "hbi-cfg__addon-kicker", "Compatible add-on"));
      body.appendChild(el("span", "hbi-cfg__addon-name", addOnConfig.family?.label || wrapperHandle));
      card.appendChild(body);
    }

    card.appendChild(el("span", "hbi-cfg__addon-price", formatMoney(variant.price)));

    if (!variant.available) {
      card.classList.add("hbi-cfg__addon--oos");
      card.appendChild(el("p", "hbi-cfg__addon-hint", "Currently out of stock."));
      delete addOnChecked[wrapperHandle]; // can't add what isn't in stock
      return;
    }

    const checkWrap = el("label", "hbi-cfg__addon-check-wrap");
    const check = doc.createElement("input");
    check.type = "checkbox";
    check.className = "hbi-cfg__addon-check";
    check.checked = !!addOnChecked[wrapperHandle];
    check.addEventListener("change", () => {
      addOnChecked[wrapperHandle] = check.checked;
      updateAddOnNote();
      emitLayoutPreviewEvent();
      updateAddOns(); // another add-on may be gated on this one (e.g. LSS on PSS)
    });
    checkWrap.appendChild(check);
    card.appendChild(checkWrap);
  }

  // "+ [name] ([price])" next to the Add to Cart button, updating the
  // instant a checkbox is toggled - not just after submission. Fixes the
  // exact checkbox-to-CTA disconnect found reviewing Selleasy's own widget.
  function updateAddOnNote() {
    const parts = [];
    for (const handle in addOnChecked) {
      if (!addOnChecked[handle]) continue;
      const r = addOnResolved[handle];
      if (r) parts.push(`${r.label} (${formatMoney(r.variant.price)})`);
    }
    addOnNote.hidden = parts.length === 0;
    addOnNote.textContent = parts.length ? `+ ${parts.join(", ")}` : "";
  }

  // Bench Layout Preview: broadcast current width/length/quantity/add-on
  // state so a diagram rendered in a totally different Liquid block (below
  // the gallery, not in this buy box) can stay in sync. No-op when the
  // family hasn't opted in, so this costs nothing for every other family.
  function emitLayoutPreviewEvent() {
    if (!config.layoutPreview) return;
    const addOnActive = Object.keys(addOnChecked).some(
      (handle) => addOnChecked[handle] && addOnResolved[handle]
    );
    const detail = {
      widthFt: parseFeetKey(selections.width),
      lengthFt: parseFeetKey(selections.length),
      quantity,
      addOnActive,
    };
    // Track rendering (TD/TN, 2026-10-07): only meaningful for families with
    // a required companion - every other family's diagram ignores these
    // being absent, same opt-in spirit as layoutPreview itself.
    if (config.requiredCompanion) {
      detail.roomWidthFt = roomWidthFt;
      detail.trackLengthInches = companionLengthInches(config, roomWidthFt);
      detail.trackQty = companionQty(config, selections);
    }
    root.dispatchEvent(
      new (root.ownerDocument.defaultView.CustomEvent)("hbi:selectionchange", {
        bubbles: true,
        detail,
      })
    );
  }

  function highlightMissing() {
    showMissing = true;
    renderFields();
    void updateResult();
    const first = fields.querySelector(
      ".hbi-cfg__field--missing select, .hbi-cfg__field--missing .hbi-cfg__pill-input"
    );
    if (first && first.focus) first.focus();
  }

  async function onAddClick() {
    if (busy) return;
    if (!resolved) {
      // Early click: point at what's missing instead of doing nothing.
      highlightMissing();
      return;
    }
    if (!resolved.variant.available) return;
    // Required companion must also be resolved+in-stock before this can
    // proceed - a track bench can't be added without its required track
    // (recomputeAddReady already keeps the button disabled for this case;
    // this is the same "never a broken or misleading buy button" stance
    // applied directly, not just trusted via button state).
    if (config.requiredCompanion && (!companionResolved || !companionResolved.variant.available)) return;
    busy = true;
    addBtn.disabled = true;
    setAddBtnContent("Adding...", true);

    // Checked + resolved + in-stock add-ons ride in the SAME cart/add.js
    // call as the primary item - one click, one atomic add. Quantity
    // matches the primary's (one add-on per bench, e.g. one PSS per bench).
    const extraItems = [];
    const addedAddOns = []; // for the dispatched event only, not the cart POST
    for (const handle in addOnChecked) {
      if (!addOnChecked[handle]) continue;
      const r = addOnResolved[handle];
      if (r && r.variant.available) {
        extraItems.push({ id: r.variant.variantId, quantity });
        addedAddOns.push({ sku: r.sku, quantity });
      }
    }
    // Required companion (TD/TN floor track): its OWN derived quantity, never
    // the bench's `quantity` above - see companionQty's doc comment. Gated
    // the same way the primary is (recomputeAddReady already blocks this
    // click entirely when it isn't resolved+available, but double-check here
    // too rather than trust button state alone).
    if (config.requiredCompanion && companionResolved && companionResolved.variant.available) {
      extraItems.push({ id: companionResolved.variant.variantId, quantity: companionResolved.qty });
      addedAddOns.push({ sku: companionResolved.sku, quantity: companionResolved.qty });
    }

    let res;
    try {
      res = await adapter.addToCart(resolved.variantId, quantity, resolved.sku, extraItems);
    } catch (err) {
      res = { ok: false, error: String(err) };
    }
    busy = false;
    addBtn.disabled = false;
    setAddBtnContent("Add to cart", true);
    if (res && res.ok) {
      root.dispatchEvent(
        new (root.ownerDocument.defaultView.CustomEvent)("hbi:added", {
          bubbles: true,
          detail: { sku: resolved.sku, quantity, addOns: addedAddOns },
        })
      );
    } else {
      renderResultFallback();
    }
  }

  renderFields();
  void updateResult();
  void updateAddOns();
  void updateRequiredCompanion();

  return {
    state: () => ({ selections: { ...selections }, resolved, addOnChecked: { ...addOnChecked } }),
    destroy: () => (root.textContent = ""),
  };
}

/**
 * Grouped/Bundle UI - environment-agnostic rendering over the logic engine,
 * sibling to configurator-ui.js's createConfigurator but for schemaVersion 2
 * (an optional Series gate over one or more multi-select Sections). Kept as
 * a separate module rather than folded into configurator-ui.js since the two
 * shapes render completely different DOM - see
 * docs/status-2026-09-04-grouped-bundle-plan.md for the full design record.
 *
 * The host page supplies an adapter:
 *   getVariantByHandle(handle, sku) -> Promise<{ price, available, variantId, image } | null>
 *       Grouped items carry their own real Shopify handle directly (unlike
 *       the Configurator's handlePrefix+sku convention) since these
 *       products are pre-existing and never NetSuite-synced/renamed.
 *   addToCart(variantId, quantity, sku, extraItems) -> Promise<{ ok, error? }>
 *       SAME signature as configurator-ui.js's adapter - the first selected
 *       item rides as the "primary" argument, every other selected item
 *       rides in extraItems, so this reuses the exact same
 *       /cart/add.js-with-an-items-array mechanism as Configurator Add-Ons.
 *       One known limitation: theme-adapter.js's wrapper-link-back property
 *       only attaches to the primary item, not extraItems - acceptable for
 *       v1 (every line still adds correctly; only one line links back).
 *
 * Failure stance matches configurator-ui.js: an invalid config degrades to
 * the quote fallback link, never a broken/half-working picker. A single
 * item's variant fetch failing removes just that item's price/availability
 * (shown as unavailable) rather than breaking the whole section.
 *
 * "Your System" hero (the Grouped/Bundle equivalent of Bench Layout
 * Preview's white-space-under-the-image slot): dispatches a bubbling
 * "hbi:groupedchange" CustomEvent on `root` on every selection/series
 * change, detail { seriesKey, seriesLabel, sections: [{ key, label, items:
 * [{ sku, name, qty, image }] }] } - only sections with at least one
 * selected item are included. The hero itself lives in a separate Liquid
 * block below the image gallery (see hbi-grouped-hero.liquid) and listens
 * on `document`, decoupled the same way Bench Layout Preview is.
 */

// Named distinctly from configurator-ui.js's own instanceCounter - both
// files are concatenated into one script scope by tools/build_theme.py
// (import/export stripped, not real ES modules at runtime), so two
// same-named top-level `let` declarations would collide.
let groupedInstanceCounter = 0;

function createGroupedBuilder(root, config, adapter, opts = {}) {
  const fallbackUrl = opts.fallbackUrl || "/pages/contact";
  const fallbackLabel = opts.fallbackLabel || "Request a quote";
  const supportPhone = opts.supportPhone || "888-815-9763";
  const supportEmail = opts.supportEmail || "support@hydrobuilder.com";
  const doc = root.ownerDocument;
  const instanceId = ++groupedInstanceCounter; // namespaces the series radio group name

  function el(tag, className, text) {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function renderFallback(message) {
    root.textContent = "";
    const box = el("div", "hbi-grp__fallback");
    box.appendChild(el("p", "hbi-grp__fallback-msg", message));
    const link = el("a", "hbi-grp__fallback-link", fallbackLabel);
    link.href = fallbackUrl;
    box.appendChild(link);
    root.appendChild(box);
  }

  const check = validateConfig(config);
  if (!check.valid) {
    renderFallback("This product's builder is temporarily unavailable.");
    return { state: () => null, destroy: () => (root.textContent = "") };
  }

  let seriesKey = hasSeries(config) ? null : undefined; // undefined = "no series step at all"
  let selected = {}; // sku -> qty
  let openSectionKey = (config.sections[0] || {}).key || null;
  let busy = false;
  // Option groups (2026-10-09): current chip picks per group key. Picks only
  // choose which variant the row is showing - quantities still live in
  // `selected` per SKU, so a customer can add 24" assemblies, switch the row
  // to 36", and add those too; both stay in the build.
  const picks = {};
  function picksFor(group) {
    if (!picks[group.key]) picks[group.key] = groupDefaultPicks(group);
    return picks[group.key];
  }

  const variantCache = new Map(); // sku -> { price, available, variantId, image } | null

  function itemBySku(sku) {
    for (const section of sectionsForSeries(config, seriesKey)) {
      const item = section.concreteItems.find((i) => i.sku === sku);
      if (item) return item;
    }
    return null;
  }

  // "**bold**" in tip text -> <strong>, everything else as plain text nodes
  // (never innerHTML - tip text is merchandiser-authored config content).
  function richText(parent, text) {
    String(text)
      .split(/(\*\*[^*]+\*\*)/)
      .forEach((part) => {
        if (!part) return;
        if (/^\*\*[^*]+\*\*$/.test(part)) parent.appendChild(el("strong", null, part.slice(2, -2)));
        else parent.appendChild(doc.createTextNode(part));
      });
    return parent;
  }

  function tipEl(tip) {
    const box = el("div", `hbi-grp__tip hbi-grp__tip--${tip.level}`);
    box.setAttribute("role", tip.level === "warn" ? "alert" : "note");
    box.appendChild(el("span", "hbi-grp__tip-icon", tip.level === "warn" ? "!" : tip.level === "ok" ? "✓" : "i"));
    box.appendChild(richText(el("span", "hbi-grp__tip-text"), tip.text));
    return box;
  }

  const root_ = el("div", "hbi-grp");
  root.textContent = "";
  root.appendChild(root_);

  const seriesEl = el("div", "hbi-grp__series");
  const sectionsEl = el("div", "hbi-grp__sections");
  const heroChain = el("div", "hbi-grp__summary");
  const helpBlock = el("div", "hbi-grp__help");
  const modal = buildHelpModal();

  root_.appendChild(seriesEl);
  root_.appendChild(sectionsEl);
  root_.appendChild(heroChain);
  root_.appendChild(helpBlock);
  root_.appendChild(modal.el);

  buildHelpBlock();

  function svgPhone() {
    const svg = doc.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "1.8");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    const path = doc.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute(
      "d",
      "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
    );
    svg.appendChild(path);
    return svg;
  }

  function svgEnvelope() {
    const svg = doc.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "1.8");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    const rect = doc.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", "2");
    rect.setAttribute("y", "4");
    rect.setAttribute("width", "20");
    rect.setAttribute("height", "16");
    rect.setAttribute("rx", "2");
    const path = doc.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M22 6 12 13 2 6");
    svg.appendChild(rect);
    svg.appendChild(path);
    return svg;
  }

  function buildHelpBlock() {
    helpBlock.textContent = "";
    helpBlock.appendChild(el("div", "hbi-grp__help-title", "Need help?"));
    helpBlock.appendChild(
      el("p", "hbi-grp__help-copy", "Our team can help you spec the right system for your setup.")
    );
    const phoneLink = el("a", "hbi-grp__help-link");
    phoneLink.href = `tel:${supportPhone.replace(/[^\d+]/g, "")}`;
    phoneLink.appendChild(svgPhone());
    phoneLink.appendChild(doc.createTextNode(`Call ${supportPhone}`));
    const emailLink = el("a", "hbi-grp__help-link");
    emailLink.href = `mailto:${supportEmail}`;
    emailLink.appendChild(svgEnvelope());
    emailLink.appendChild(doc.createTextNode(supportEmail));
    helpBlock.appendChild(phoneLink);
    helpBlock.appendChild(emailLink);
  }

  function buildHelpModal() {
    const backdrop = el("div", "hbi-grp__modal-backdrop");
    backdrop.hidden = true;
    const card = el("div", "hbi-grp__modal-card");
    const title = el("h3", "hbi-grp__modal-title");
    const body = el("p", "hbi-grp__modal-body");
    const closeBtn = el("button", "hbi-grp__modal-close", "Close");
    closeBtn.type = "button";
    card.appendChild(title);
    card.appendChild(body);
    card.appendChild(closeBtn);
    backdrop.appendChild(card);
    let lastFocused = null;
    function open(promptTitle, promptBody) {
      title.textContent = promptTitle;
      body.textContent = promptBody;
      lastFocused = doc.activeElement;
      backdrop.hidden = false;
      closeBtn.focus();
    }
    function close() {
      backdrop.hidden = true;
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }
    closeBtn.addEventListener("click", close);
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close();
    });
    doc.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !backdrop.hidden) close();
    });
    return { el: backdrop, open, close };
  }

  function svgChevron() {
    const svg = doc.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 20 20");
    svg.setAttribute("class", "hbi-grp__section-chevron");
    const path = doc.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M5 7.5 10 12.5 15 7.5");
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.6");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    svg.appendChild(path);
    return svg;
  }

  function renderSeries() {
    seriesEl.textContent = "";
    if (!hasSeries(config)) return;
    const group = el("div", "hbi-grp__series-group");
    for (const s of config.series) {
      const label = el("label", "hbi-grp__series-card" + (seriesKey === s.key ? " hbi-grp__series-card--selected" : ""));
      const input = doc.createElement("input");
      input.type = "radio";
      input.name = `hbi-grp-series-${instanceId}`;
      input.className = "hbi-grp__series-input";
      input.checked = seriesKey === s.key;
      input.addEventListener("change", () => {
        if (seriesKey !== s.key) {
          seriesKey = s.key;
          selected = autoSelectedDefaults(config, seriesKey);
          renderAll();
        }
      });
      label.appendChild(input);
      const body = el("div", "hbi-grp__series-body");
      body.appendChild(el("div", "hbi-grp__series-name", s.label));
      if (s.spec) body.appendChild(el("div", "hbi-grp__series-spec", s.spec));
      if (s.description) body.appendChild(el("div", "hbi-grp__series-desc", s.description));
      label.appendChild(body);
      group.appendChild(label);
    }
    seriesEl.appendChild(group);
  }

  function fetchVariant(item) {
    if (variantCache.has(item.sku)) return;
    variantCache.set(item.sku, "pending");
    // Always re-render on resolution, regardless of what's rendered since
    // this fetch started. renderAll() only ever reads current seriesKey/
    // selected/variantCache state fresh, so there's no stale-data risk from
    // rendering "late" - dropping this render on some staleness condition
    // (a prior version compared against a per-render sequence counter) just
    // silently lost updates whenever two fetches resolved close together:
    // the SECOND one's variantCache write would never get reflected in the
    // DOM until some unrelated click forced another render. That's what
    // made a closed section's rows look permanently stuck on "Checking
    // availability..." even once actually fetched - confirmed live and
    // reproduced deterministically with a local test harness.
    Promise.resolve(adapter.getVariantByHandle(item.handle, item.sku))
      .catch(() => null)
      .then((variant) => {
        variantCache.set(item.sku, variant || null);
        renderAll();
      });
  }

  function itemRow(item, sectionKey) {
    const qty = selected[item.sku] || 0;
    const isSelected = qty > 0;
    const row = el("div", "hbi-grp__row" + (isSelected ? " hbi-grp__row--selected" : ""));
    row.setAttribute("role", "button");
    row.tabIndex = 0;

    const variant = variantCache.get(item.sku);
    const thumb = el("div", "hbi-grp__row-thumb");
    if (variant && variant !== "pending" && variant.image) {
      const img = doc.createElement("img");
      img.src = variant.image;
      img.alt = "";
      thumb.appendChild(img);
    }
    row.appendChild(thumb);

    const body = el("div", "hbi-grp__row-body");
    body.appendChild(el("div", "hbi-grp__row-name", variant && variant !== "pending" && variant.title ? variant.title : item.name));
    const metaBits = [];
    if (item.spec) metaBits.push(item.spec);
    if (variant === "pending") metaBits.push("Checking availability…");
    else if (variant === null) metaBits.push("Unavailable");
    if (metaBits.length) body.appendChild(el("div", "hbi-grp__row-meta", metaBits.join(" · ")));
    if (item.description) body.appendChild(el("div", "hbi-grp__row-desc", item.description));
    row.appendChild(body);

    const priceEl = el(
      "div",
      "hbi-grp__row-price",
      variant && variant !== "pending" ? formatMoneyLocal(variant.price) : ""
    );
    row.appendChild(priceEl);

    const controls = el("div", "hbi-grp__row-controls");
    const unavailable = variant === null || (variant && variant !== "pending" && !variant.available);
    if (isSelected) {
      const stepper = el("div", "hbi-grp__qty-stepper");
      const dec = el("button", "hbi-grp__qty-btn", "−");
      dec.type = "button";
      const val = el("span", "hbi-grp__qty-val", String(qty));
      const inc = el("button", "hbi-grp__qty-btn", "+");
      inc.type = "button";
      dec.addEventListener("click", (e) => {
        e.stopPropagation();
        changeQty(item.sku, -1);
      });
      inc.addEventListener("click", (e) => {
        e.stopPropagation();
        changeQty(item.sku, 1);
      });
      stepper.appendChild(dec);
      stepper.appendChild(val);
      stepper.appendChild(inc);
      controls.appendChild(stepper);
    } else {
      // An empty ring - a plain "click to select" affordance, not a
      // checkmark. Selection itself is conveyed by the border/tint
      // (.hbi-grp__row--selected) plus the qty stepper replacing this mark
      // entirely once selected (see .hbi-grp__row--selected .hbi-grp__row-mark
      // in hbi-grouped-buybox.liquid). A checkmark icon used to render here
      // unconditionally for every available row, making unselected items
      // look already checked.
      const mark = el("div", "hbi-grp__row-mark");
      controls.appendChild(mark);
    }
    row.appendChild(controls);

    if (unavailable) {
      row.classList.add("hbi-grp__row--unavailable");
    } else {
      const toggle = () => {
        if (selected[item.sku]) delete selected[item.sku];
        else selected[item.sku] = 1;
        renderAll();
      };
      row.addEventListener("click", toggle);
      row.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      });
    }

    return row;
  }

  function groupRow(group, section) {
    const p = picksFor(group);
    const variant = groupVariant(group, p);
    const concrete = variant ? section.concreteItems.find((i) => i.sku === variant.sku) : null;
    const wrap = el("div", "hbi-grp__group");

    if (concrete) {
      // The resolved variant renders as a normal row (thumb, price, qty),
      // titled with the group's name + the picked values.
      const row = itemRow({ ...concrete, name: group.name, spec: [groupVariantLabel(group, variant), variant.spec ?? group.spec].filter(Boolean).join(" · ") }, section.key);
      if (group.description) {
        const d = row.querySelector(".hbi-grp__row-desc");
        if (!d) row.querySelector(".hbi-grp__row-body").appendChild(el("div", "hbi-grp__row-desc", group.description));
      }
      wrap.appendChild(row);
    } else {
      wrap.appendChild(el("div", "hbi-grp__row hbi-grp__row--unavailable", `${group.name} - that combination isn't available.`));
    }

    const pickers = el("div", "hbi-grp__group-pickers");
    for (const opt of group.options) {
      const line = el("div", "hbi-grp__group-opt");
      line.appendChild(el("span", "hbi-grp__group-opt-label", opt.label));
      const chips = el("div", "hbi-grp__chips");
      for (const v of opt.values) {
        const avail = groupValueAvailable(group, p, opt.key, v.key);
        const on = p[opt.key] === v.key;
        const chipBtn = el("button", "hbi-grp__chip" + (on ? " hbi-grp__chip--on" : "") + (avail ? "" : " hbi-grp__chip--na"));
        chipBtn.type = "button";
        chipBtn.setAttribute("aria-pressed", on ? "true" : "false");
        if (!avail) chipBtn.title = "Not available with the other options picked - choosing it switches them";
        if (v.swatch) {
          const sw = el("span", "hbi-grp__swatch");
          sw.style.background = v.swatch;
          chipBtn.appendChild(sw);
        }
        chipBtn.appendChild(doc.createTextNode(v.label));
        chipBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          picks[group.key] = groupRepick(group, { ...p, [opt.key]: v.key }, opt.key);
          renderAll();
        });
        chips.appendChild(chipBtn);
      }
      line.appendChild(chips);
      pickers.appendChild(line);
    }
    wrap.appendChild(pickers);

    // Other variants of this group already in the build (picked, then the
    // row switched to a different size) - listed so they're never hidden.
    const others = section.concreteItems.filter(
      (i) => i.groupKey === group.key && (!variant || i.sku !== variant.sku) && (selected[i.sku] || 0) > 0
    );
    if (others.length) {
      const list = el("div", "hbi-grp__group-others");
      list.appendChild(el("span", "hbi-grp__group-others-label", "Also in your build:"));
      for (const o of others) {
        const chipEl = el("span", "hbi-grp__group-other");
        const label = groupVariantLabel(group, o);
        const show = el("button", "hbi-grp__group-other-show", `${label} ×${selected[o.sku]}`);
        show.type = "button";
        show.title = "Show this option";
        show.addEventListener("click", () => {
          picks[group.key] = { ...o.values };
          renderAll();
        });
        const rm = el("button", "hbi-grp__group-other-rm", "×");
        rm.type = "button";
        rm.setAttribute("aria-label", `Remove ${label}`);
        rm.addEventListener("click", () => {
          delete selected[o.sku];
          renderAll();
        });
        chipEl.appendChild(show);
        chipEl.appendChild(rm);
        list.appendChild(chipEl);
      }
      wrap.appendChild(list);
    }
    return wrap;
  }

  function changeQty(sku, delta) {
    const next = (selected[sku] || 0) + delta;
    if (next <= 0) delete selected[sku];
    else selected[sku] = next;
    renderAll();
  }

  function renderSections() {
    sectionsEl.textContent = "";
    if (hasSeries(config) && seriesKey == null) {
      sectionsEl.appendChild(
        el("div", "hbi-grp__locked", "Select an option above to see compatible components.")
      );
      return;
    }

    const tips = activeTips(config, seriesKey, selected);
    for (const section of sectionsForSeries(config, seriesKey)) {
      const count = section.concreteItems.filter((i) => (selected[i.sku] || 0) > 0).length;
      const sectionTips = tips.filter((t) => t.section === section.key);
      const det = doc.createElement("details");
      det.className = "hbi-grp__section";
      det.open = openSectionKey === section.key;

      const summary = el("summary", "hbi-grp__section-head");
      summary.appendChild(el("span", "hbi-grp__section-label", section.label));
      summary.appendChild(el("span", "hbi-grp__pill" + (section.required ? " hbi-grp__pill--required" : ""), section.required ? "Required" : "Optional"));
      // Single spacer absorbs all free space so [count pill (if any)][chevron] sit flush
      // right, consistently regardless of label length - see hbi-grp__section-spacer in
      // hbi-grouped-buybox.liquid for why this replaced margin-left: auto on two separate
      // elements (that split the free space between them unevenly per row).
      summary.appendChild(el("span", "hbi-grp__section-spacer"));
      if (sectionTips.some((t) => t.level === "warn")) summary.appendChild(el("span", "hbi-grp__pill hbi-grp__pill--warn", "Check"));
      if (count) summary.appendChild(el("span", "hbi-grp__pill hbi-grp__pill--selected", `${count} selected`));
      summary.appendChild(svgChevron());
      det.appendChild(summary);

      const body = el("div", "hbi-grp__section-body");
      if (section.description) body.appendChild(el("p", "hbi-grp__section-intro", section.description));
      if (section.helpPrompt) {
        const trigger = el("button", "hbi-grp__help-trigger", section.helpPrompt.trigger);
        trigger.type = "button";
        trigger.addEventListener("click", (e) => {
          e.stopPropagation();
          modal.open(section.helpPrompt.title, section.helpPrompt.body);
        });
        body.appendChild(trigger);
      }
      const list = el("div", "hbi-grp__row-list");
      for (const item of section.items) {
        list.appendChild(isOptionGroup(item) ? groupRow(item, section) : itemRow(item, section.key));
      }
      body.appendChild(list);
      if (sectionTips.length) {
        const tipList = el("div", "hbi-grp__tips");
        sectionTips.forEach((t) => tipList.appendChild(tipEl(t)));
        body.appendChild(tipList);
      }
      det.appendChild(body);

      det.addEventListener("toggle", () => {
        // The toggle event fires as an asynchronously queued task per the
        // HTML spec (for both user clicks and script-set .open), and this
        // render function replaces every <details> element on each render
        // (needed so a fresh render always reflects openSectionKey). That
        // combination means a STALE element's toggle event can fire after
        // it's already been removed from the DOM - without this guard, its
        // "close every other section" side effect below would run against
        // the CURRENT live accordion and close everything, since the stale
        // `det` never matches any of today's elements. Caught by
        // test_bundled_theme_asset.mjs, not a hypothetical.
        if (!det.isConnected) return;
        if (det.open) {
          // Only a genuine open (this section wasn't already the open one)
          // should scroll - re-affirming the same section's open state
          // (e.g. the initial render, or a qty-change re-render while it's
          // already open) must not, or every quantity click would yank the
          // page back to the section header.
          const isNewlyOpened = openSectionKey !== section.key;
          openSectionKey = section.key;
          sectionsEl.querySelectorAll(".hbi-grp__section").forEach((other) => {
            if (other !== det) other.open = false;
          });
          if (isNewlyOpened) {
            // Don't rely on the browser's own native scroll-into-view
            // compensation: it sizes its adjustment for whichever section's
            // height changed most in this same synchronous step (usually
            // the section that just collapsed), not the one that just
            // opened - which overshoots badly past a short section (e.g.
            // Precision Dosers, 2 items) collapsing a long one (e.g.
            // Dosers) at the same time. Wait a frame for that native
            // layout/scroll settling to finish, then take explicit control.
            const view = doc.defaultView;
            const thisSectionKey = section.key;
            // Looks up the currently-open section fresh from the live DOM
            // rather than closing over `det`/`summary` - renderAll() (see
            // its own comment above) replaces every <details> element on
            // each render, so a reference captured here would go stale the
            // moment a late-arriving fetch below triggers a re-render.
            const scrollOpenSectionIntoView = () => {
              const liveDet = sectionsEl.querySelector(".hbi-grp__section[open]");
              if (!liveDet || openSectionKey !== thisSectionKey) return;
              const liveSummary = liveDet.querySelector(".hbi-grp__section-head");
              if (!liveSummary) return;
              // Plain scrollIntoView({block:'start'}) would land the
              // section header right at the viewport's top edge, which on
              // this theme is directly behind the sticky site header -
              // offset by the real measured header height (the theme sets
              // this custom property to the header's own clientHeight, see
              // sections/header.liquid) so the header stays visible.
              const headerHeight =
                parseFloat(view.getComputedStyle(doc.documentElement).getPropertyValue("--header-height")) || 0;
              const rect = liveSummary.getBoundingClientRect();
              const targetTop = view.scrollY + rect.top - headerHeight - 12;
              view.scrollTo({ top: Math.max(targetTop, 0), behavior: "smooth" });
            };
            if (view && typeof view.requestAnimationFrame === "function") {
              view.requestAnimationFrame(scrollOpenSectionIntoView);
            }
            // Each row's price/image resolves asynchronously (fetchVariant)
            // and calls renderAll() on arrival, which can change this
            // section's height after the frame above already settled the
            // scroll - confirmed live: a section landed in the wrong place
            // only on its FIRST open; opening it again (once its fetches had
            // already resolved) always landed correctly. A couple of fixed
            // re-checks catches the common case (a handful of fetches
            // resolving within ~1s) without polling indefinitely.
            if (view && typeof view.setTimeout === "function") {
              view.setTimeout(scrollOpenSectionIntoView, 400);
              view.setTimeout(scrollOpenSectionIntoView, 1000);
            }
          }
        } else if (openSectionKey === section.key) {
          openSectionKey = null;
        }
      });

      sectionsEl.appendChild(det);

      // Fetch every section's items, not just the currently-open one -
      // confirmed live: a customer had to open each section in turn before
      // its rows showed real images/prices, since a closed section's items
      // were never requested at all. fetchVariant already dedupes by SKU
      // (variantCache.has(item.sku) guard) and most SKUs repeat across
      // sections/series, so this doesn't multiply real network requests by
      // the number of sections - it just stops waiting for a click to start
      // the ones that were going to be needed anyway.
      // Option groups: only the variant each row is showing plus any already
      // selected - not every variant of every group up front (a single
      // group can hold 20+ products).
      for (const item of section.items) {
        if (!isOptionGroup(item)) {
          fetchVariant(item);
          continue;
        }
        const v = groupVariant(item, picksFor(item));
        for (const c of section.concreteItems) {
          if (c.groupKey === item.key && ((v && c.sku === v.sku) || (selected[c.sku] || 0) > 0)) fetchVariant(c);
        }
      }
    }
  }

  function renderSummary() {
    heroChain.textContent = "";
    const rows = selectedItems(config, seriesKey, selected);
    const totals = selectionTotals(config, seriesKey, selected);

    if (!rows.length) {
      // With build-savings tiers, the empty state IS the savings panel (same
      // look it keeps once items are picked) - and the series prompt above
      // already says "Select an option", so no second "pick an option" line.
      const panel = savingsPanel(0, savingsFor([]));
      if (panel) heroChain.appendChild(panel);
      else {
        heroChain.appendChild(
          el(
            "div",
            "hbi-grp__empty",
            hasSeries(config) && seriesKey == null
              ? "Pick an option above to get started."
              : "Select at least one component below."
          )
        );
      }
    } else {
      const list = el("div", "hbi-grp__summary-list");
      let total = 0;
      for (const row of rows) {
        const variant = variantCache.get(row.item.sku);
        const price = variant && variant !== "pending" ? variant.price : null;
        if (typeof price === "number") total += price * row.qty;
        const line = el("div", "hbi-grp__summary-line");
        const thumb = el("div", "hbi-grp__summary-thumb");
        if (variant && variant !== "pending" && variant.image) {
          const img = doc.createElement("img");
          img.src = variant.image;
          img.alt = "";
          thumb.appendChild(img);
        }
        line.appendChild(thumb);
        const liveRow = variantCache.get(row.item.sku);
        line.appendChild(el("span", "hbi-grp__summary-name", `${liveRow && liveRow !== "pending" && liveRow.title ? liveRow.title : row.item.name}${row.qty > 1 ? ` ×${row.qty}` : ""}`));
        line.appendChild(el("span", "hbi-grp__summary-price", typeof price === "number" ? formatMoneyLocal(price * row.qty) : ""));
        list.appendChild(line);
      }
      heroChain.appendChild(list);
      const totalRow = el("div", "hbi-grp__summary-total");
      const totalLeft = el("span", "hbi-grp__summary-total-left");
      totalLeft.appendChild(el("span", null, `${totals.totalQty} unit${totals.totalQty === 1 ? "" : "s"}`));
      const clearBtn = el("button", "hbi-grp__clear-btn", "Clear selections");
      clearBtn.type = "button";
      clearBtn.addEventListener("click", () => {
        selected = {};
        renderAll();
      });
      totalLeft.appendChild(clearBtn);
      totalRow.appendChild(totalLeft);
      const sav = savingsFor(rows);
      if (sav.current) {
        const sub = el("div", "hbi-grp__summary-sub");
        sub.appendChild(el("span", null, "Subtotal"));
        sub.appendChild(el("span", null, formatMoneyLocal(total)));
        heroChain.appendChild(sub);
        const save = el("div", "hbi-grp__summary-sub hbi-grp__summary-save");
        save.appendChild(el("span", null, `Build savings (${sav.current.percent}%)`));
        save.appendChild(el("span", null, "−" + formatMoneyLocal(sav.cents)));
        heroChain.appendChild(save);
      }
      totalRow.appendChild(el("span", "hbi-grp__summary-total-value", formatMoneyLocal(total - sav.cents)));
      heroChain.appendChild(totalRow);
      const panel = savingsPanel(total, sav);
      if (panel) heroChain.appendChild(panel);
    }

    const openTips = activeTips(config, seriesKey, selected).filter((t) => t.level !== "ok");
    if (rows.length && openTips.length) {
      const box = el("div", "hbi-grp__summary-tips");
      box.appendChild(el("div", "hbi-grp__summary-tips-title", "Suggestions for your build"));
      openTips.forEach((t) => box.appendChild(tipEl(t)));
      heroChain.appendChild(box);
    }

    const unmet = unmetRequiredSections(config, seriesKey, selected);
    const ready = canAddToCart(config, seriesKey, selected);
    const addBtn = el("button", "hbi-grp__add", busy ? "Adding…" : "Add build to cart");
    addBtn.type = "button";
    addBtn.setAttribute("aria-disabled", ready ? "false" : "true");
    addBtn.disabled = busy;
    addBtn.addEventListener("click", onAddClick);
    heroChain.appendChild(addBtn);

    if (!ready) {
      const sectionLabels = sectionsForSeries(config, seriesKey)
        .filter((s) => unmet.includes(s.key))
        .map((s) => s.label);
      const hint = sectionLabels.length
        ? `Select at least one item from: ${sectionLabels.join(", ")}`
        : "Select at least one item to continue";
      heroChain.appendChild(el("div", "hbi-grp__add-hint", hint));
    }
  }

  /* ── Build savings (2026-10-12) ──
   * Optional config.discounts { tiers: [{ min: <dollars>, percent }], note }.
   * DISPLAY ONLY - the real discount is a Shopify automatic discount with the
   * same numbers (e.g. "TrolMaster Build Savings 3%"). Savings are computed
   * per unit, rounded down, exactly like Shopify's "Amount off products", so
   * the summary matches checkout to the cent. Same feature as the full-width
   * Creator page (ui/creator-ui.js), sized for the compact buy box. */
  const tiers = (config.discounts && Array.isArray(config.discounts.tiers) ? config.discounts.tiers : [])
    .filter((t) => t && t.min > 0 && t.percent > 0)
    .sort((a, b) => a.min - b.min);
  let lastTierPct = 0;
  function savingsFor(rows) {
    let subtotal = 0;
    for (const r of rows) {
      const v = variantCache.get(r.item.sku);
      if (v && v !== "pending" && typeof v.price === "number") subtotal += v.price * r.qty;
    }
    let current = null, next = null;
    for (const t of tiers) {
      if (subtotal >= t.min * 100) current = t;
      else if (!next) next = t;
    }
    let cents = 0;
    if (current) {
      for (const r of rows) {
        const v = variantCache.get(r.item.sku);
        if (v && v !== "pending" && typeof v.price === "number") cents += r.qty * Math.floor((v.price * current.percent) / 100);
      }
    }
    return { subtotal, current, next, cents };
  }
  function money0(dollars) {
    return "$" + Number(dollars).toLocaleString("en-US", { maximumFractionDigits: 0 });
  }
  function savingsPanel(subtotal, sav) {
    if (!tiers.length) return null;
    const box = el("div", "hbi-grp__savings" + (sav.current ? " hbi-grp__savings--on" : ""));
    const head = el("div", "hbi-grp__savings-head");
    head.appendChild(el("span", "hbi-grp__savings-badge" + (sav.current ? "" : " hbi-grp__savings-badge--lock"), sav.current ? `${sav.current.percent}% OFF` : "BUILD & SAVE"));
    const msg = el("span", "hbi-grp__savings-msg");
    const b = (t) => el("b", null, t);
    if (!subtotal) {
      const top = tiers[tiers.length - 1];
      msg.append("Save up to ", b(`${top.percent}%`), " on your build: ", tiers.map((t) => `${t.percent}% at ${money0(t.min)}`).join(", ") + ".");
    } else if (!sav.current && sav.next) {
      msg.append("Add ", b(formatMoneyLocal(sav.next.min * 100 - subtotal)), " more to save ", b(`${sav.next.percent}%`), " on your build.");
    } else if (sav.current && sav.next) {
      msg.append("You save ", b(formatMoneyLocal(sav.cents)), ". Add ", b(formatMoneyLocal(sav.next.min * 100 - subtotal)), " more to save ", b(`${sav.next.percent}%`), ".");
    } else {
      msg.append("You save ", b(formatMoneyLocal(sav.cents)), " - our best build savings.");
    }
    head.appendChild(msg);
    box.appendChild(head);
    // progress: markers evenly spaced (thresholds can be far apart)
    const n = tiers.length;
    let pos = 0;
    for (let i = 0; i < n; i++) {
      const lo = i === 0 ? 0 : tiers[i - 1].min * 100, hi = tiers[i].min * 100;
      if (subtotal >= hi) pos = (i + 1) / n;
      else { pos = (i + Math.max(0, subtotal - lo) / (hi - lo)) / n; break; }
    }
    const bar = el("div", "hbi-grp__savings-bar");
    const fill = el("div", "hbi-grp__savings-fill");
    fill.style.width = `${Math.min(100, pos * 100).toFixed(1)}%`;
    bar.appendChild(fill);
    tiers.forEach((t, i) => {
      const mark = el("span", "hbi-grp__savings-mark" + (subtotal >= t.min * 100 ? " is-hit" : "") + (sav.next === t ? " is-next" : ""));
      mark.style.left = `${((i + 1) / n) * 100}%`;
      mark.appendChild(el("i"));
      mark.appendChild(el("em", null, `${t.percent}%`));
      mark.appendChild(el("small", null, money0(t.min)));
      bar.appendChild(mark);
    });
    box.appendChild(bar);
    if (config.discounts.note) box.appendChild(el("p", "hbi-grp__savings-note", config.discounts.note));
    const pct = sav.current ? sav.current.percent : 0;
    if (pct > lastTierPct) {
      box.classList.add("is-unlocked");
      box.appendChild(el("div", "hbi-grp__savings-toast", `🎉 ${pct}% build savings unlocked!`));
    }
    lastTierPct = pct;
    return box;
  }

  function emitGroupedChangeEvent() {
    const seriesMeta = hasSeries(config) ? config.series.find((s) => s.key === seriesKey) : null;
    const bySection = [];
    for (const section of sectionsForSeries(config, seriesKey)) {
      const items = section.concreteItems
        .filter((i) => (selected[i.sku] || 0) > 0)
        .map((i) => {
          const variant = variantCache.get(i.sku);
          return {
            sku: i.sku,
            name: i.name,
            qty: selected[i.sku],
            image: variant && variant !== "pending" ? variant.image : null,
          };
        });
      if (items.length) bySection.push({ key: section.key, label: section.label, items });
    }
    root.dispatchEvent(
      new (root.ownerDocument.defaultView.CustomEvent)("hbi:groupedchange", {
        bubbles: true,
        detail: {
          seriesKey: seriesKey ?? null,
          seriesLabel: seriesMeta ? seriesMeta.label : null,
          sections: bySection,
        },
      })
    );
  }

  async function onAddClick() {
    if (busy) return;
    if (!canAddToCart(config, seriesKey, selected)) return;
    busy = true;
    renderAll();

    const rows = selectedItems(config, seriesKey, selected);
    const resolved = [];
    for (const row of rows) {
      let variant = variantCache.get(row.item.sku);
      if (variant === undefined || variant === "pending") {
        try {
          variant = await adapter.getVariantByHandle(row.item.handle, row.item.sku);
        } catch {
          variant = null;
        }
        variantCache.set(row.item.sku, variant || null);
      }
      if (variant && variant.available) resolved.push({ sku: row.item.sku, qty: row.qty, variantId: variant.variantId });
    }

    busy = false;
    if (!resolved.length) {
      renderAll();
      return;
    }

    const [primary, ...rest] = resolved;
    const extraItems = rest.map((r) => ({ id: r.variantId, quantity: r.qty }));
    let res;
    try {
      res = await adapter.addToCart(primary.variantId, primary.qty, primary.sku, extraItems);
    } catch (err) {
      res = { ok: false, error: String(err) };
    }
    if (res && res.ok) {
      root.dispatchEvent(
        new (root.ownerDocument.defaultView.CustomEvent)("hbi:added", {
          bubbles: true,
          detail: { quantity: resolved.reduce((n, r) => n + r.qty, 0) },
        })
      );
    }
    renderAll();
  }

  function formatMoneyLocal(cents) {
    if (typeof cents !== "number" || !isFinite(cents)) return "";
    const dollars = Math.floor(cents / 100);
    const rem = String(cents % 100).padStart(2, "0");
    return `$${dollars.toLocaleString("en-US")}.${rem}`;
  }

  function renderAll() {
    renderSeries();
    renderSections();
    renderSummary();
    emitGroupedChangeEvent();
  }

  // "hbi:groupedset" (2026-10-09): lets a separate block on the same page -
  // first case, the Netafim quick-start calculator below the gallery - fill
  // in a whole suggested build. detail: { familyKey?, seriesKey?, selected:
  // { sku: qty }, picks?: { groupKey: { optionKey: valueKey } } }. Replaces
  // the current selection; SKUs this builder doesn't list are ignored.
  // Decoupled by event, same as the "Your System" hero.
  doc.addEventListener("hbi:groupedset", (e) => {
    const d = e.detail || {};
    if (d.familyKey && config.family && d.familyKey !== config.family.key) return;
    if (hasSeries(config) && d.seriesKey && config.series.some((s) => s.key === d.seriesKey)) seriesKey = d.seriesKey;
    const next = {};
    for (const [sku, qty] of Object.entries(d.selected || {})) {
      const n = Math.floor(Number(qty));
      if (n > 0 && itemBySku(sku)) next[sku] = Math.min(n, 9999);
    }
    selected = next;
    for (const [k, v] of Object.entries(d.picks || {})) picks[k] = { ...v };
    openSectionKey = d.openSection || openSectionKey;
    renderAll();
  });

  renderAll();

  return {
    state: () => ({ seriesKey, selected: { ...selected }, picks: JSON.parse(JSON.stringify(picks)) }),
    destroy: () => (root.textContent = ""),
  };
}

/**
 * Full-width Creator page UI (2026-10-09, first case: Netafim Drip System
 * Creator) - a second renderer for the SAME Grouped/Bundle (schemaVersion 2)
 * config the compact buy-box builder (grouped-ui.js) uses, laid out like the
 * standalone prototype Dustin preferred: water-flow diagram, card grid with
 * chip pickers, tabbed build paths and a sticky "Your system" summary. It is
 * mounted by theme/sections/hbi-creator.liquid on the product.creator
 * template, which replaces main-product for that wrapper only - Dosatron and
 * every other grouped wrapper keep the compact buy box.
 *
 * Same state model as grouped-ui.js (selected: sku -> qty, picks per option
 * group), same engine helpers, same adapter (getVariantByHandle live per
 * SKU, addToCart with extraItems), same "hbi:groupedset" event from the
 * quick-start block. Optional config `page` block drives the layout:
 *   page: { flow?: { title?, hint?, steps: [{ label, sub?, sections?: [key], external?: true }] },
 *           tabs?: { eyebrow?, title?, intro?, paths: [{ key, label, sub?, pill?, sections: [key] }] } }
 * Without it, every section simply renders in config order.
 * Items/variants may carry `pack` (units per purchase) + `unit` (singular
 * noun) for a "per unit" price line.
 */

function createCreatorPage(root, config, adapter, opts = {}) {
  const doc = root.ownerDocument;
  const view = doc.defaultView;
  const phone = opts.supportPhone || "888-815-9763";
  const phoneHref = `tel:+1${phone.replace(/\D/g, "").replace(/^1/, "")}`;

  const check = validateConfig(config);
  if (!check.valid || config.schemaVersion !== 2 || Array.isArray(config.series)) {
    root.innerHTML = `<p class="hbc-fallback">This builder is temporarily unavailable. Please call <a href="${phoneHref}">${esc(phone)}</a> and we'll put your system together for you.</p>`;
    return { state: () => null };
  }

  const sections = sectionsForSeries(config, undefined);
  const sectionByKey = Object.fromEntries(sections.map((s) => [s.key, s]));
  const page = config.page || {};
  const paths = (page.tabs && Array.isArray(page.tabs.paths) ? page.tabs.paths : []).filter((p) =>
    (p.sections || []).some((k) => sectionByKey[k])
  );
  const tabbed = new Set(paths.flatMap((p) => p.sections || []));

  let selected = {};
  const picks = {};
  let activePath = paths[0] ? paths[0].key : null;
  let busy = false;
  let message = null; // { level, html }
  const variants = new Map(); // sku -> {price, available, variantId, image} | null | "pending"

  /* ── helpers ── */
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  }
  function rich(s) {
    return esc(s).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  }
  function money(cents) {
    if (typeof cents !== "number" || !isFinite(cents)) return "";
    return "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function picksFor(group) {
    if (!picks[group.key]) picks[group.key] = groupDefaultPicks(group);
    return picks[group.key];
  }
  function concreteBySku(sku) {
    for (const s of sections) {
      const c = s.concreteItems.find((i) => i.sku === sku);
      if (c) return { item: c, section: s };
    }
    return null;
  }
  function want(item) {
    if (!item || variants.has(item.sku)) return;
    variants.set(item.sku, "pending");
    Promise.resolve(adapter.getVariantByHandle(item.handle, item.sku))
      .catch(() => null)
      .then((v) => {
        variants.set(item.sku, v || null);
        schedule();
      });
  }
  let timer = null;
  function schedule() {
    if (timer) return;
    timer = view.setTimeout(() => {
      timer = null;
      render();
    }, 50);
  }
  function variantInfo(sku) {
    const v = variants.get(sku);
    return v && v !== "pending" ? v : null;
  }
  // Display name = the product's current Shopify title once fetched (products get
  // renamed in NetSuite/Shopify; the config copy went stale - Dosatron 2026-10-27),
  // the config name until then or if the fetch fails.
  function liveName(item) {
    const v = variantInfo(item.sku);
    return v && v.title ? v.title : item.name;
  }
  function sectionCount(key) {
    const s = sectionByKey[key];
    return s ? s.concreteItems.filter((i) => (selected[i.sku] || 0) > 0).length : 0;
  }
  const ICON = {
    tip: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/></svg>',
    warn: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M12 3l9.5 17h-19z"/><path d="M12 10v4M12 17.5h.01"/></svg>',
    ok: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
  };
  function note(level, html) {
    return `<div class="hbc-note hbc-note--${level}">${ICON[level] || ICON.tip}<div>${html}</div></div>`;
  }
  function qtyCtl(sku, qty) {
    return `<div class="hbc-qty" role="group" aria-label="Quantity"><button type="button" data-q="${esc(sku)}" data-d="-1" aria-label="Decrease">−</button>` +
      `<input type="number" min="0" inputmode="numeric" value="${qty}" data-qin="${esc(sku)}" aria-label="Quantity">` +
      `<button type="button" data-q="${esc(sku)}" data-d="1" aria-label="Increase">+</button></div>`;
  }
  function perUnit(price, pack, unit) {
    if (typeof price !== "number" || !(pack > 1)) return "";
    const each = price / pack;
    const s = each < 100 ? `${(each).toFixed(1).replace(/\.0$/, "")}¢` : money(each);
    return `${s} per ${esc(unit || "unit")} · ${Number(pack).toLocaleString()} per ${pack === 1 ? "item" : "pack"}`;
  }

  function plural(word, n) {
    if (n === 1) return word;
    if (/(s|x|ch|sh)$/.test(word)) return word + "es";
    if (/[^aeiou]y$/.test(word)) return word.slice(0, -1) + "ies";
    return word + "s";
  }

  /* ── cards ── */
  function priceBlock(sku, pack, unit, fallbackSpec) {
    const v = variantInfo(sku);
    if (!v) return `<div class="hbc-price"><b class="hbc-price--wait">${variants.get(sku) === null ? "Unavailable" : "…"}</b></div>`;
    const sub = perUnit(v.price, pack, unit) || esc(fallbackSpec || "");
    return `<div class="hbc-price"><b>${money(v.price)}${v.available ? "" : ' <span class="hbc-oos">· Out of stock</span>'}</b>${sub ? `<small>${sub}</small>` : ""}</div>`;
  }
  function footer(sku, available) {
    const qty = selected[sku] || 0;
    if (!available) return "";
    return qty > 0 ? qtyCtl(sku, qty) : `<button type="button" class="hbc-btn hbc-btn--line hbc-btn--sm" data-q="${esc(sku)}" data-d="1">Add to build</button>`;
  }
  function thumb(sku) {
    const v = variantInfo(sku);
    return `<div class="hbc-thumb">${v && v.image ? `<img src="${esc(v.image)}" alt="" loading="lazy" width="80" height="80">` : ""}</div>`;
  }
  function plainCard(item) {
    want(item);
    const v = variantInfo(item.sku);
    const inB = (selected[item.sku] || 0) > 0;
    return `<div class="hbc-card${inB ? " is-in" : ""}" id="hbc-card-${esc(item.key || item.sku)}">` +
      `<div class="hbc-card-top">${thumb(item.sku)}<div class="hbc-card-head">${item.spec ? `<div class="hbc-kicker">${esc(item.spec)}</div>` : ""}<h3>${esc(liveName(item))}</h3>${item.description ? `<p class="hbc-desc">${esc(item.description)}</p>` : ""}</div></div>` +
      `<div class="hbc-card-foot">${priceBlock(item.sku, item.pack, item.unit, "")}${footer(item.sku, !v || v.available)}</div></div>`;
  }
  function groupCard(group, section) {
    const p = picksFor(group);
    const variant = groupVariant(group, p);
    if (variant) want(section.concreteItems.find((c) => c.sku === variant.sku));
    const v = variant ? variantInfo(variant.sku) : null;
    const inB = variant && (selected[variant.sku] || 0) > 0;
    const anyIn = section.concreteItems.some((c) => c.groupKey === group.key && (selected[c.sku] || 0) > 0);
    let h = `<div class="hbc-card${inB || anyIn ? " is-in" : ""}" id="hbc-card-${esc(group.key)}">`;
    h += `<div class="hbc-card-top">${variant ? thumb(variant.sku) : '<div class="hbc-thumb"></div>'}<div class="hbc-card-head">` +
      `<div class="hbc-kicker">${esc(variant ? [groupVariantLabel(group, variant), variant.spec || group.spec].filter(Boolean).join(" · ") : group.spec || "")}</div>` +
      `<h3>${esc(v && v.title ? v.title : group.name)}</h3>${group.description ? `<p class="hbc-desc">${esc(group.description)}</p>` : ""}</div></div>`;
    h += '<div class="hbc-picks">';
    for (const opt of group.options) {
      h += `<div><div class="hbc-pick-label">${esc(opt.label)}</div><div class="hbc-chips">`;
      for (const val of opt.values) {
        const on = p[opt.key] === val.key;
        const ok = groupValueAvailable(group, p, opt.key, val.key);
        h += `<button type="button" class="hbc-chip${ok ? "" : " hbc-chip--na"}" aria-pressed="${on}" data-group="${esc(group.key)}" data-opt="${esc(opt.key)}" data-val="${esc(val.key)}"${ok ? "" : ' title="Not available with the other options - picking it switches them"'}>` +
          `${val.swatch ? `<span class="hbc-swatch" style="background:${esc(val.swatch)}"></span>` : ""}${esc(val.label)}</button>`;
      }
      h += "</div></div>";
    }
    h += "</div>";
    h += '<div class="hbc-card-foot">';
    if (!variant) h += '<span class="hbc-oos">That combination isn\'t available - pick another option.</span>';
    else h += priceBlock(variant.sku, variant.pack ?? group.pack, group.unit, "") + footer(variant.sku, !v || v.available);
    h += "</div>";
    const others = section.concreteItems.filter((c) => c.groupKey === group.key && (!variant || c.sku !== variant.sku) && (selected[c.sku] || 0) > 0);
    if (others.length) {
      h += '<div class="hbc-others"><span>Also in your build:</span>' + others.map((o) =>
        `<span class="hbc-other"><button type="button" data-show="${esc(group.key)}" data-sku="${esc(o.sku)}">${esc(groupVariantLabel(group, o))} ×${selected[o.sku]}</button><button type="button" data-rm="${esc(o.sku)}" aria-label="Remove">×</button></span>`
      ).join("") + "</div>";
    }
    return h + "</div>";
  }
  function rowsCard(section) {
    // 3+ plain items, no groups -> one condensed card with a row per item
    const rows = section.items.map((item) => {
      want(item);
      const v = variantInfo(item.sku);
      return `<div class="hbc-row" id="hbc-card-${esc(item.key || item.sku)}">${thumb(item.sku)}<div class="hbc-row-name">${esc(liveName(item))}<small>${esc([item.spec, item.description].filter(Boolean).join(" · "))}</small></div>` +
        `<div class="hbc-row-price">${v ? money(v.price) + (v.available ? "" : '<br><small class="hbc-oos">Out of stock</small>') : ""}</div>${qtyCtl(item.sku, selected[item.sku] || 0)}</div>`;
    }).join("");
    const anyIn = section.items.some((i) => (selected[i.sku] || 0) > 0);
    return `<div class="hbc-card${anyIn ? " is-in" : ""}"><div class="hbc-rows">${rows}</div></div>`;
  }
  function sectionCards(section) {
    const groups = section.items.filter(isOptionGroup);
    if (!groups.length && section.items.length >= 3) return `<div class="hbc-cards">${rowsCard(section)}</div>`;
    const cards = section.items.map((i) => (isOptionGroup(i) ? groupCard(i, section) : plainCard(i)));
    return `<div class="hbc-cards${cards.length > 1 ? " hbc-cards--2" : ""}">${cards.join("")}</div>`;
  }
  function sectionTips(key, tips) {
    return tips.filter((t) => t.section === key).map((t) => note(t.level, rich(t.text))).join("");
  }
  function sectionBlock(section, tips, step) {
    return `<section class="hbc-sec" id="hbc-sec-${esc(section.key)}"><div class="hbc-sec-head"><div class="hbc-eyebrow">Step ${step}</div><h2>${esc(section.label)}</h2>${section.description ? `<p>${esc(section.description)}</p>` : ""}</div>` +
      sectionCards(section) + sectionTips(section.key, tips) + "</section>";
  }
  function pathsBlock(tips, step) {
    const t = page.tabs || {};
    let h = `<section class="hbc-sec" id="hbc-sec-paths"><div class="hbc-sec-head"><div class="hbc-eyebrow">Step ${step}${t.eyebrow ? " · " + esc(t.eyebrow) : ""}</div><h2>${esc(t.title || "Choose your path")}</h2>${t.intro ? `<p>${esc(t.intro)}</p>` : ""}</div>`;
    h += '<div class="hbc-tabs" role="tablist">';
    for (const p of paths) {
      const n = (p.sections || []).reduce((a, k) => a + sectionCount(k), 0);
      h += `<button type="button" class="hbc-tab" role="tab" aria-selected="${p.key === activePath}" data-path="${esc(p.key)}">` +
        (n ? `<span class="hbc-pill hbc-pill--count">${n} in build</span>` : p.pill ? `<span class="hbc-pill">${esc(p.pill)}</span>` : "") +
        `<b>${esc(p.label)}</b>${p.sub ? `<span>${esc(p.sub)}</span>` : ""}</button>`;
    }
    h += "</div>";
    const cur = paths.find((p) => p.key === activePath) || paths[0];
    for (const key of cur.sections || []) {
      const s = sectionByKey[key];
      if (!s) continue;
      h += `<div class="hbc-subsec" id="hbc-sec-${esc(s.key)}"><h3 class="hbc-subsec-title">${esc(s.label)}</h3>${s.description ? `<p class="hbc-subsec-intro">${esc(s.description)}</p>` : ""}` +
        sectionCards(s) + sectionTips(s.key, tips) + "</div>";
    }
    return h + "</section>";
  }

  /* ── flow diagram ── */
  function flowBlock() {
    const f = page.flow;
    if (!f || !Array.isArray(f.steps) || !f.steps.length) return "";
    const steps = f.steps;
    const W = 980, H = 132, n = steps.length, bw = Math.min(150, (W - 20 - (n - 1) * 24) / n), gap = n > 1 ? (W - 20 - n * bw) / (n - 1) : 0, y = 24, bh = 64;
    let svg = `<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(steps.map((s) => s.label).join(", then "))}"><defs><marker id="hbc-arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M0 0L10 5L0 10z" fill="#1C4643"/></marker></defs>`;
    steps.forEach((s, i) => {
      const x = 10 + i * (bw + gap);
      const on = !s.external && (s.sections || []).some((k) => sectionCount(k) > 0);
      const target = !s.external && (s.sections || []).find((k) => sectionByKey[k]);
      if (i < n - 1) svg += `<line x1="${x + bw + 4}" y1="${y + bh / 2}" x2="${x + bw + gap - 6}" y2="${y + bh / 2}" stroke="#1C4643" stroke-width="2.5"${on || s.external ? "" : ' stroke-dasharray="5 4"'} marker-end="url(#hbc-arr)"/>`;
      const fill = s.external ? "#F5F7F6" : on ? "#1C4643" : "#FFFFFF", stroke = s.external ? "#9CA3AF" : on ? "#1C4643" : "#D5DDDB";
      const tc = on ? "#FFFFFF" : "#111827", sc = on ? "rgba(255,255,255,.78)" : "#6B7280";
      svg += `<g${target ? ` class="hbc-node" tabindex="0" role="link" data-go="${esc(target)}" aria-label="${esc(s.label)} - jump to parts"` : ""}>` +
        `<rect x="${x}" y="${y}" width="${bw}" height="${bh}" rx="12" fill="${fill}" stroke="${stroke}" stroke-width="1.5"${s.external ? ' stroke-dasharray="5 4"' : ""}/>` +
        `<text x="${x + bw / 2}" y="${y + 28}" text-anchor="middle" font-family="system-ui,-apple-system,Segoe UI,Arial,sans-serif" font-size="14" font-weight="800" fill="${tc}">${esc(s.label)}</text>` +
        (s.sub ? `<text x="${x + bw / 2}" y="${y + 47}" text-anchor="middle" font-family="system-ui,-apple-system,Segoe UI,Arial,sans-serif" font-size="11.5" fill="${sc}">${esc(s.sub)}</text>` : "") +
        (on ? `<circle cx="${x + bw - 10}" cy="${y + 10}" r="8" fill="#b65d21"/><path d="M${x + bw - 14} ${y + 10}l3 3 5-6" stroke="#fff" stroke-width="2" fill="none"/>` : "") + "</g>";
      svg += `<text x="${x + bw / 2}" y="${y + bh + 22}" text-anchor="middle" font-family="system-ui,-apple-system,Segoe UI,Arial,sans-serif" font-size="11" font-weight="700" fill="#6B7280">${s.external ? "your equipment" : on ? "in your build" : "tap to add"}</text>`;
    });
    svg += "</svg>";
    return `<div class="hbc-panel hbc-flow"><div class="hbc-panel-head"><h2>${esc(f.title || "How the water flows")}</h2><span>${esc(f.hint || "Tap a stage to jump to its parts")}</span></div><div class="hbc-flow-scroll">${svg}</div>` +
      '<div class="hbc-flow-legend"><span><i class="on"></i>In your build</span><span><i></i>Not added yet</span><span><i class="ext"></i>Your equipment</span></div></div>';
  }

  /* ── build savings tiers (optional config.discounts) ──
   * { tiers: [{ min: 500, percent: 3 }, ...], note?: string }
   * DISPLAY ONLY: the actual discount is a Shopify automatic discount (or a
   * discount Function) configured with the same numbers - this just shows
   * the customer where they stand. min is in dollars. */
  const tiers = (config.discounts && Array.isArray(config.discounts.tiers) ? config.discounts.tiers : [])
    .filter((t) => t && t.min > 0 && t.percent > 0)
    .sort((a, b) => a.min - b.min);
  let lastPos = 0, lastSavings = 0, lastTier = 0, celebrateTier = null;
  const reduceMotion = view.matchMedia && view.matchMedia("(prefers-reduced-motion: reduce)").matches;
  function animateTiers(totalCents) {
    const box = sumSlot.querySelector(".hbc-tiers");
    if (!box) return;
    const fill = box.querySelector(".hbc-tiers-fill");
    const { current, savings } = tierInfo(totalCents);
    if (fill) {
      const to = parseFloat(fill.dataset.to);
      if (reduceMotion) fill.style.width = to + "%";
      else view.requestAnimationFrame(() => view.requestAnimationFrame(() => { fill.style.width = to + "%"; }));
      lastPos = to;
    }
    const cu = box.querySelector(".hbc-countup");
    if (cu) {
      const from = lastSavings, target = Number(cu.dataset.to), t0 = view.performance.now(), dur = reduceMotion ? 0 : 700;
      const step = (t) => {
        const k = dur ? Math.min(1, (t - t0) / dur) : 1;
        cu.textContent = money(Math.round(from + (target - from) * (1 - Math.pow(1 - k, 3))));
        if (k < 1) view.requestAnimationFrame(step);
      };
      view.requestAnimationFrame(step);
    }
    lastSavings = savings;
    const pct = current ? current.percent : 0;
    if (pct > lastTier && celebrateTier !== false) celebrate(box, pct);
    lastTier = pct;
  }
  function celebrate(box, pct) {
    box.classList.add("is-unlocked");
    const toast = doc.createElement("div");
    toast.className = "hbc-unlock";
    toast.setAttribute("role", "status");
    toast.textContent = `🎉 ${pct}% build savings unlocked!`;
    box.appendChild(toast);
    if (!reduceMotion) {
      const colors = ["#b65d21", "#F5C8A0", "#1C4643", "#6EE7B7", "#FCD34D"];
      for (let i = 0; i < 18; i++) {
        const c = doc.createElement("span");
        c.className = "hbc-confetti";
        const a = (Math.PI * 2 * i) / 18, d = 50 + Math.random() * 60;
        c.style.setProperty("--dx", `${Math.cos(a) * d}px`);
        c.style.setProperty("--dy", `${Math.sin(a) * d - 30}px`);
        c.style.setProperty("--rot", `${Math.random() * 540 - 270}deg`);
        c.style.background = colors[i % colors.length];
        box.appendChild(c);
      }
    }
    view.setTimeout(() => { toast.remove(); box.querySelectorAll(".hbc-confetti").forEach((c) => c.remove()); box.classList.remove("is-unlocked"); }, 2600);
  }
  function tierInfo(totalCents) {
    let current = null, next = null;
    for (const t of tiers) {
      if (totalCents >= t.min * 100) current = t;
      else if (!next) next = t;
    }
    return { current, next, savings: current ? savingsAt(current.percent) : 0 };
  }
  // Match Shopify's "Amount off products" math exactly: the percentage is
  // applied PER UNIT and each unit's discount is rounded DOWN to the cent
  // (verified 2026-10-10: a $507.85 build -> $15.16 at checkout, not
  // 3% x subtotal = $15.24). Uses live prices of the selected lines.
  function savingsAt(percent) {
    let cents = 0;
    for (const r of selectedItems(config, undefined, selected)) {
      const v = variantInfo(r.item.sku);
      if (v && typeof v.price === "number") cents += r.qty * Math.floor((v.price * percent) / 100);
    }
    return cents;
  }
  function tierBlock(totalCents) {
    if (!tiers.length) return "";
    const { current, next, savings } = tierInfo(totalCents);
    const top = tiers[tiers.length - 1].min * 100;
    // markers spaced evenly (thresholds can be far apart, e.g. $500 vs $5,000)
    const n = tiers.length;
    let pos = 0;
    for (let i = 0; i < n; i++) {
      const lo = i === 0 ? 0 : tiers[i - 1].min * 100, hi = tiers[i].min * 100;
      if (totalCents >= hi) pos = (i + 1) / n;
      else { pos = (i + Math.max(0, totalCents - lo) / (hi - lo)) / n; break; }
    }
    let msg;
    if (!current && next) msg = `Add <b>${money(next.min * 100 - totalCents)}</b> more to save <b>${next.percent}%</b> on your build.`;
    else if (current && next) msg = `You're saving <b>${current.percent}%</b> (about ${money(savings)}). Add <b>${money(next.min * 100 - totalCents)}</b> more to save <b>${next.percent}%</b>.`;
    else msg = `You're saving <b>${current.percent}%</b> on your build - about ${money(savings)}. That's our best build savings.`;
    const marks = tiers.map((t, i) => `<span class="hbc-tier-mark${totalCents >= t.min * 100 ? " is-hit" : ""}" style="left:${((i + 1) / n) * 100}%"><i></i><em>${t.percent}%</em><small>$${t.min >= 1000 ? (t.min / 1000) + "k" : t.min}</small></span>`).join("");
    const to = Math.min(100, pos * 100).toFixed(1);
    const marksAnim = marks.replace(new RegExp(`(class="hbc-tier-mark)(" style="left:${((tiers.indexOf(next) + 1) / n) * 100}%")`), "$1 is-next$2");
    const head = current
      ? `<div class="hbc-tiers-head"><span class="hbc-tiers-badge">${current.percent}% OFF</span><span class="hbc-tiers-saved">You save <b class="hbc-countup" data-to="${savings}">${money(lastSavings)}</b></span></div>`
      : `<div class="hbc-tiers-head"><span class="hbc-tiers-badge hbc-tiers-badge--lock">BUILD & SAVE</span><span class="hbc-tiers-saved">Up to <b>${tiers[tiers.length - 1].percent}% off</b> your system</span></div>`;
    return `<div class="hbc-tiers${current ? " has-tier" : ""}">${head}<div class="hbc-tiers-msg">${msg}</div><div class="hbc-tiers-bar"><div class="hbc-tiers-fill" data-to="${to}" style="width:${lastPos}%"></div>${next ? marksAnim : marks}</div>` +
      (config.discounts.note ? `<p class="hbc-tiers-note">${esc(config.discounts.note)}</p>` : "") + "</div>";
  }

  /* ── summary ── */
  function summaryBlock(tips) {
    const rows = selectedItems(config, undefined, selected);
    let total = 0, missing = false;
    let h = `<div class="hbc-sum-head"><h2>Your system</h2><p>${rows.length ? `${rows.length} part${rows.length > 1 ? "s" : ""} · nothing is required` : "Use the planner above or add parts below"}</p></div><div class="hbc-sum-body">`;
    if (message) h += note(message.level, message.html);
    if (!rows.length) h += '<p class="hbc-sum-empty">Your build is empty. Start with <a href="#hbc-quick">your grow details</a> for a suggested system, or add parts from each step.</p>';
    let last = "";
    for (const r of rows) {
      const sec = sectionByKey[r.sectionKey];
      if (sec && sec.label !== last) { last = sec.label; h += `<div class="hbc-sum-group">${esc(last)}</div>`; }
      const v = variantInfo(r.item.sku);
      if (v) total += v.price * r.qty; else missing = true;
      if (!v) want(r.item);
      const pk = r.item.pack || 1;
      const qtyTxt = pk > 1 ? `${r.qty} ${r.qty === 1 ? "pack" : "packs"} ×` : `${r.qty} ×`;
      const detail = pk > 1 ? `${pk.toLocaleString()} per pack · ${(pk * r.qty).toLocaleString()} ${esc(plural(r.item.unit || "unit", pk * r.qty))}` : esc(r.item.spec || "");
      h += `<div class="hbc-sum-line"><span class="n">${qtyTxt} ${esc(liveName(r.item))}</span><span class="p">${v ? money(v.price * r.qty) : "…"}</span>` +
        `<span class="d">${detail}</span><button type="button" class="x" data-rm="${esc(r.item.sku)}">Remove</button></div>`;
    }
    if (rows.length) {
      const { current, savings } = tierInfo(total);
      if (current) {
        h += `<div class="hbc-sum-sub"><span>Subtotal</span><span>${money(total)}</span></div>`;
        h += `<div class="hbc-sum-sub hbc-sum-save"><span>Build savings (${current.percent}%)</span><span>−${money(savings)}</span></div>`;
      }
      h += `<div class="hbc-sum-total"><span>Total</span><b>${money(total - savings)}${missing ? "*" : ""}</b></div>`;
    }
    h += tierBlock(total);
    const ready = canAddToCart(config, undefined, selected);
    h += `<div class="hbc-sum-actions"><button type="button" class="hbc-btn hbc-btn--primary" data-act="cart"${ready && !busy ? "" : " disabled"}>${busy ? "Adding…" : "Add system to cart"}</button>` +
      `<div class="hbc-sum-row"><button type="button" class="hbc-btn hbc-btn--line hbc-btn--sm" data-act="share"${rows.length ? "" : " disabled"}>Copy share link</button><button type="button" class="hbc-btn hbc-btn--line hbc-btn--sm" data-act="reset"${rows.length ? "" : " disabled"}>Start over</button></div></div>`;
    h += '<div class="hbc-sum-msg" aria-live="polite"></div>';
    const open = tips.filter((t) => t.level !== "ok");
    if (rows.length && open.length) {
      h += `<div class="hbc-sum-checks"><h4>Suggestions for your build</h4>${open.slice(0, 4).map((t) => note(t.level, rich(t.text))).join("")}${open.length > 4 ? `<p class="hbc-hint">+ ${open.length - 4} more in the steps</p>` : ""}</div>`;
    } else if (rows.length) {
      h += note("ok", "Looks complete - no suggestions.");
    }
    h += `<div class="hbc-sum-quote">Big room or facility? Call <a href="${phoneHref}">${esc(phone)}</a> for case pricing and a design review.</div>`;
    return { html: h + "</div>", total, count: rows.length };
  }

  /* ── render ── */
  const shell = doc.createElement("div");
  shell.className = "hbc-builder";
  shell.innerHTML = '<div class="hbc-main"><div class="hbc-flow-slot"></div><div class="hbc-sections"></div></div><aside class="hbc-panel hbc-summary" aria-label="Your system"></aside>';
  root.textContent = "";
  root.appendChild(shell);
  const mbar = doc.createElement("div");
  mbar.className = "hbc-mbar";
  root.appendChild(mbar);
  const flowSlot = shell.querySelector(".hbc-flow-slot");
  const secSlot = shell.querySelector(".hbc-sections");
  const sumSlot = shell.querySelector(".hbc-summary");

  function render() {
    const tips = activeTips(config, undefined, selected);
    flowSlot.innerHTML = flowBlock();
    let h = "", step = 0, pathsDone = false;
    for (const s of sections) {
      if (tabbed.has(s.key)) {
        if (!pathsDone && paths.length) { h += pathsBlock(tips, ++step); pathsDone = true; }
        continue;
      }
      h += sectionBlock(s, tips, ++step);
    }
    const hint = page.buildHint || "Tap the option buttons on a part to pick a size or flow rate, then <b>Add to build</b>. Everything you pick collects in <b>Your system</b>" + (view.matchMedia && view.matchMedia("(min-width: 1080px)").matches ? " on the right" : " below") + " - nothing is required, and tips point out anything you might be missing.";
    secSlot.innerHTML = `<div class="hbc-build-hint">${ICON.tip}<div>${page.buildHint ? esc(hint) : hint}</div></div>` + h;
    const sum = summaryBlock(tips);
    sumSlot.innerHTML = sum.html;
    animateTiers(sum.total);
    const ti = tierInfo(sum.total);
    const mNote = !tiers.length ? `${sum.count} part${sum.count === 1 ? "" : "s"} in your system`
      : ti.current ? `Saving ${ti.current.percent}% on your build` : ti.next && sum.count ? `${money(ti.next.min * 100 - sum.total)} more to save ${ti.next.percent}%` : `${sum.count} parts in your system`;
    mbar.innerHTML = `<div><b>${money(sum.total - ti.savings) || "$0.00"}</b>${ti.current ? `<span class="hbc-mbar-badge">-${ti.current.percent}%</span>` : ""}<small>${mNote}</small></div><a class="hbc-btn hbc-btn--primary hbc-btn--sm" href="#" data-act="to-summary">Review &amp; add to cart</a>`;
  }

  function scrollToEl(el, block) {
    if (!el) return;
    const hh = parseFloat(view.getComputedStyle(doc.documentElement).getPropertyValue("--header-height")) || 0;
    const top = el.getBoundingClientRect().top + view.scrollY - hh - 12;
    if (block === "center") el.scrollIntoView({ behavior: "smooth", block: "center" });
    else view.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }
  function setQty(sku, n) {
    n = Math.max(0, Math.min(9999, Math.floor(n) || 0));
    if (n) selected[sku] = n; else delete selected[sku];
    message = null;
    render();
  }
  function setMsg(level, html) {
    const el = sumSlot.querySelector(".hbc-sum-msg");
    if (el) el.innerHTML = note(level, html);
  }
  function pathOf(sectionKey) {
    const p = paths.find((x) => (x.sections || []).includes(sectionKey));
    return p ? p.key : null;
  }

  /* share link: ?build=<sku:qty,...> (plain, readable, SKU-based) */
  function shareUrl() {
    const b = Object.entries(selected).map(([s, q]) => `${s}:${q}`).join(",");
    return `${view.location.origin}${view.location.pathname}?build=${encodeURIComponent(b)}`;
  }
  function loadShared() {
    const m = view.location.search.match(/[?&]build=([^&]+)/);
    if (!m) return;
    const next = {};
    decodeURIComponent(m[1]).split(",").forEach((pair) => {
      const [sku, q] = pair.split(":");
      const hit = concreteBySku(sku);
      const n = parseInt(q, 10);
      if (hit && n > 0) {
        next[sku] = Math.min(n, 9999);
        if (hit.item.groupKey) picks[hit.item.groupKey] = { ...hit.item.values };
      }
    });
    if (Object.keys(next).length) {
      selected = next;
      message = { level: "ok", html: "Loaded a shared build - change anything you like." };
    }
  }

  async function addToCart() {
    if (busy || !canAddToCart(config, undefined, selected)) return;
    busy = true;
    render();
    const rows = selectedItems(config, undefined, selected);
    const lines = [], skipped = [];
    for (const r of rows) {
      let v = variantInfo(r.item.sku);
      if (!v) {
        try { v = await adapter.getVariantByHandle(r.item.handle, r.item.sku); } catch { v = null; }
        variants.set(r.item.sku, v || null);
      }
      if (v && v.available) lines.push({ id: v.variantId, quantity: r.qty, sku: r.item.sku });
      else skipped.push(liveName(r.item));
    }
    let res = { ok: false };
    if (lines.length) {
      const [first, ...rest] = lines;
      try {
        res = await adapter.addToCart(first.id, first.quantity, first.sku, rest.map((l) => ({ id: l.id, quantity: l.quantity })));
      } catch (e) {
        res = { ok: false, error: String(e) };
      }
    }
    busy = false;
    render();
    if (res && res.ok) {
      root.dispatchEvent(new view.CustomEvent("hbi:added", { bubbles: true, detail: { quantity: lines.reduce((n, l) => n + l.quantity, 0) } }));
      setMsg(skipped.length ? "warn" : "ok", skipped.length ? `Added to your cart. Not added (out of stock): ${esc(skipped.join(", "))}.` : "Your system is in the cart.");
    } else {
      setMsg("warn", `Something went wrong adding your system. Please try again or call <a href="${phoneHref}">${esc(phone)}</a>.`);
    }
  }

  root.addEventListener("click", (e) => {
    const b = e.target.closest("button, a[data-act], [data-go]");
    if (!b || !root.contains(b)) return;
    if (b.dataset.group) {
      const g = sections.flatMap((s) => s.items).find((i) => i.key === b.dataset.group);
      if (g) picks[g.key] = groupRepick(g, { ...picksFor(g), [b.dataset.opt]: b.dataset.val }, b.dataset.opt);
      render();
      return;
    }
    if (b.dataset.q) return setQty(b.dataset.q, (selected[b.dataset.q] || 0) + Number(b.dataset.d));
    if (b.dataset.rm) return setQty(b.dataset.rm, 0);
    if (b.dataset.show) {
      const hit = concreteBySku(b.dataset.sku);
      if (hit) picks[b.dataset.show] = { ...hit.item.values };
      render();
      return;
    }
    if (b.dataset.path) { activePath = b.dataset.path; render(); return; }
    if (b.dataset.go) {
      const p = pathOf(b.dataset.go);
      if (p && p !== activePath) { activePath = p; render(); }
      scrollToEl(root.querySelector(`#hbc-sec-${CSS.escape(b.dataset.go)}`));
      return;
    }
    const act = b.dataset.act;
    if (act === "cart") return addToCart();
    if (act === "reset") { selected = {}; message = null; try { view.history.replaceState(null, "", view.location.pathname); } catch {} render(); return; }
    if (act === "to-summary") { e.preventDefault(); scrollToEl(sumSlot); return; }
    if (act === "share") {
      const url = shareUrl();
      try { view.history.replaceState(null, "", url); } catch {}
      const fallback = () => setMsg("tip", `Copy this link:<br><small style="word-break:break-all">${esc(url)}</small>`);
      if (view.navigator.clipboard && view.navigator.clipboard.writeText) view.navigator.clipboard.writeText(url).then(() => setMsg("ok", "Link copied - anyone with it sees this exact build."), fallback);
      else fallback();
    }
  });
  root.addEventListener("keydown", (e) => {
    const g = e.target.closest && e.target.closest("[data-go]");
    if (g && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); g.dispatchEvent(new view.MouseEvent("click", { bubbles: true })); }
  });
  root.addEventListener("change", (e) => {
    const t = e.target;
    if (t.dataset && t.dataset.qin) setQty(t.dataset.qin, parseInt(t.value, 10) || 0);
  });

  // Quick-start block (hbi-grouped-quickstart.js) -> fill the whole build
  doc.addEventListener("hbi:groupedset", (e) => {
    const d = e.detail || {};
    if (d.familyKey && config.family && d.familyKey !== config.family.key) return;
    const next = {};
    for (const [sku, qty] of Object.entries(d.selected || {})) {
      const n = Math.floor(Number(qty));
      if (n > 0 && concreteBySku(sku)) next[sku] = Math.min(n, 9999);
    }
    selected = next;
    for (const [k, v] of Object.entries(d.picks || {})) picks[k] = { ...v };
    // jump the tabs to the path the suggestion used
    for (const sku of Object.keys(next)) {
      const hit = concreteBySku(sku);
      const p = hit && pathOf(hit.section.key);
      if (p) { activePath = p; break; }
    }
    message = { level: "ok", html: "Suggested build added. Review each step, then add it to your cart." };
    render();
  });

  loadShared();
  render();
  return { state: () => ({ selected: { ...selected }, picks: JSON.parse(JSON.stringify(picks)), activePath }) };
}

/**
 * Shopify theme adapter for the configurator UI.
 *
 * Loaded as a module from the hbi-configurator.liquid section. Reads the
 * config JSON embedded by Liquid, wires the UI to live Shopify endpoints:
 *   - price/stock: /products/{handle}.js (always live, never cached in
 *     metafields - requirements Rev 2.0)
 *   - cart add:    /cart/add.js with the variant id found on that live
 *     product's variants (matched by SKU)
 *
 * No SKU -> variant/handle map is stored in the config (that blew past
 * Shopify's 128KB metafield limit at ~1,700 SKUs). Instead each SKU's
 * product handle is computed at render time from config.handlePrefix,
 * which requires the real products' handles to follow that convention
 * (see tools/gen_handle_rename_import.py for the one-time rename).
 *
 * Configurator Add-Ons (2026-08-24): an add-on's own config (options +
 * matrix) is NOT fetched live - Liquid embeds it as a sibling <script>
 * via all_products[handle], same-page, same trust level as the primary
 * config. Only that add-on's resolved SKU's price/stock/image is ever
 * fetched live, through the same getVariant() below (called with the
 * add-on's own config so handlePrefix resolves against the right family).
 *
 * This file is a SOURCE, not the shipped asset: tools/build_theme.py
 * bundles engine + UI + this adapter into ONE theme asset
 * (theme/assets/hbi-configurator.js). One file on purpose - Shopify's
 * CDN stamps a fresh ?v= onto an asset's URL when it changes, but a
 * module's `import "./other.js"` resolves to the UN-versioned URL,
 * which the CDN caches indefinitely; a multi-file build served
 * customers a stale UI module after an update (found 2026-07-22).
 * createConfigurator comes from the inlined UI module at bundle time.
 */

async function getVariant(sku, config) {
  const handle = `${config.handlePrefix || ""}${sku.toLowerCase()}`;
  const resp = await fetch(`/products/${handle}.js`);
  if (!resp.ok) return null;
  const product = await resp.json();
  const variant = (product.variants || []).find((v) => v.sku === sku);
  if (!variant) return null;
  return {
    price: variant.price,
    available: !!variant.available,
    variantId: variant.id,
    // 2026-10-27: the product's CURRENT Shopify title, so renamed products show
    // their new name without a config edit (names in the config are a fallback).
    title: product.title || null,
    // Configurator Add-Ons (2026-08-24): the add-on card's photo is this
    // same field, off this same call - getVariant already fetches the
    // add-on wrapper's own product JSON (called with ITS config, so
    // handlePrefix resolves against the right family), and product.js
    // includes featured_image for free. No separate image lookup.
    image: product.featured_image || null,
  };
}

// Grouped/Bundle (schemaVersion 2), added 2026-09-04: items carry their own
// real Shopify handle directly in the config (see grouped-ui.js) instead of
// a handlePrefix+sku convention - these products are pre-existing and never
// NetSuite-synced/renamed, so there's no prefix to reconstruct a handle
// from. Same live-fetch, never-store-price principle as getVariant above.
async function getVariantByHandle(handle, sku) {
  const resp = await fetch(`/products/${handle}.js`);
  if (!resp.ok) return null;
  const product = await resp.json();
  const variant = (product.variants || []).find((v) => v.sku === sku) || product.variants?.[0];
  if (!variant) return null;
  return {
    price: variant.price,
    available: !!variant.available,
    variantId: variant.id,
    title: product.title || null,
    image: product.featured_image || null,
  };
}

// extraItems (Configurator Add-Ons, 2026-08-24): optional array of
// { id, quantity } for any checked, resolved add-on(s), added in the SAME
// /cart/add.js call as the primary item - atomic, one "Add to Cart" click,
// matching the proven Selleasy multi-item pattern (see addons design doc).
// Adapters/callers that don't use add-ons just omit the argument.
// lineProps (2026-10-09): hidden properties stamped on EVERY line of a
// Grouped/Creator build (e.g. { _hbi_creator: "netafim-drip-system" }) so
// orders can be attributed to a creator and a future build-only discount
// Function can find build lines. Underscore keys are hidden by the cart
// snippets. Lines with properties don't merge with a plain add of the same
// variant - intended.
async function addToCart(variantId, quantity, wrapperUrl, sku, extraItems = [], lineProps = null) {
  const item = { id: variantId, quantity };
  if (wrapperUrl) {
    // Hidden property (underscore prefix = not displayed by the theme).
    // The cart line-item snippets link the line's title back to this
    // wrapper page instead of the SKU's own PDP, so customers can
    // re-configure - see main-cart.liquid / mini-cart.liquid. The URL
    // comes from Liquid ({{ product.url }} via data-wrapper-url), never
    // window.location - the theme editor's preview pane reports its own
    // location, which stamped "/cart" here during UAT (2026-07-22).
    // &sku= (2026-07-24) lands back on the exact configuration just
    // added, not a blank picker - same deep-link mechanism as VC-155's
    // redirect from a real SKU's own page.
    const url = sku ? `${wrapperUrl}&sku=${encodeURIComponent(sku)}` : wrapperUrl;
    item.properties = { _hbi_wrapper_url: url };
  }
  if (lineProps) {
    item.properties = { ...(item.properties || {}), ...lineProps };
    extraItems = extraItems.map((x) => ({ ...x, properties: { ...(x.properties || {}), ...lineProps } }));
  }
  const resp = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items: [item, ...extraItems] }),
  });
  if (!resp.ok) {
    let error = `cart add failed (${resp.status})`;
    try {
      error = (await resp.json()).description || error;
    } catch {
      /* keep default */
    }
    return { ok: false, error };
  }
  return { ok: true };
}

// Deep-link entry point: a redirect from a real SKU's own product page
// (see VC-155 / tools/gen_redirect_import.py) lands here with ?sku=<SKU>.
const initialSku = new URLSearchParams(location.search).get("sku") || undefined;
// ?room= is set by a family-switch link (TD <-> TN) so the room width carries over
const initialRoomWidthFt = parseFloat(new URLSearchParams(location.search).get("room")) || undefined;

for (const root of document.querySelectorAll("[data-hbi-configurator]")) {
  const dataEl = document.getElementById(root.dataset.hbiConfigurator);
  if (!dataEl) continue;
  let config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch {
    continue; // section renders its own noscript/fallback copy
  }
  const wrapperUrl = root.dataset.wrapperUrl || "";

  // Configurator Add-Ons (2026-08-24): each configured add-on's own config
  // (options + matrix) is embedded by Liquid as a sibling <script> - via
  // all_products[handle], so it's a same-page server-render, not a live
  // fetch (see hbi-configurator-buybox.liquid). Only price/stock/image for
  // whatever SKU that resolves to is ever fetched live, via the same
  // getVariant() used for the primary product - never store price here.
  const addOnConfigs = {};
  for (const script of dataEl.parentElement.querySelectorAll("script[data-hbi-addon-config]")) {
    const handle = script.dataset.hbiAddonConfig;
    try {
      addOnConfigs[handle] = JSON.parse(script.textContent);
    } catch {
      /* malformed/missing - that add-on silently doesn't render, per the
         "never a broken or misleading state" failure stance */
    }
  }

  createConfigurator(root, config, {
    getVariant,
    addToCart: (variantId, quantity, sku, extraItems) =>
      addToCart(variantId, quantity, wrapperUrl, sku, extraItems),
  }, {
    fallbackUrl: root.dataset.fallbackUrl || "/pages/contact",
    fallbackLabel: root.dataset.fallbackLabel || "Request a quote",
    addOnCallPhone: root.dataset.addonCallPhone || "",
    addOnConfigs,
    initialSku,
    initialRoomWidthFt,
  });
}

// Grouped/Bundle mount loop, separate from the Configurator loop above -
// [data-hbi-grouped] is a distinct attribute (not a schemaVersion check
// here) because the Liquid layer already knows which shape it's rendering
// and picks the matching snippet/attribute itself (see
// hbi-grouped-buybox.liquid vs hbi-configurator-buybox.liquid).
for (const root of document.querySelectorAll("[data-hbi-grouped]")) {
  const dataEl = document.getElementById(root.dataset.hbiGrouped);
  if (!dataEl) continue;
  let config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch {
    continue;
  }

  createGroupedBuilder(root, config, {
    getVariantByHandle,
    addToCart: (variantId, quantity, sku, extraItems) =>
      addToCart(variantId, quantity, root.dataset.wrapperUrl || "", sku, extraItems, creatorProps(config)),
  }, {
    fallbackUrl: root.dataset.fallbackUrl || "/pages/contact",
    fallbackLabel: root.dataset.fallbackLabel || "Request a quote",
    supportPhone: root.dataset.supportPhone || "888-815-9763",
    supportEmail: root.dataset.supportEmail || "support@hydrobuilder.com",
  });
}

// Full-width Creator page (2026-10-09): same grouped config, rendered by
// ui/creator-ui.js from theme/sections/hbi-creator.liquid on the
// product.creator template. Same adapter calls as the compact buy box.
for (const root of document.querySelectorAll("[data-hbi-creator]")) {
  const dataEl = document.getElementById(root.dataset.hbiCreator);
  if (!dataEl) continue;
  let config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch {
    continue;
  }
  createCreatorPage(root, config, {
    getVariantByHandle,
    addToCart: (variantId, quantity, sku, extraItems) =>
      addToCart(variantId, quantity, root.dataset.wrapperUrl || "", sku, extraItems, creatorProps(config)),
  }, {
    supportPhone: root.dataset.supportPhone || "888-815-9763",
  });
}

function creatorProps(config) {
  return config && config.family && config.family.key ? { _hbi_creator: config.family.key } : null;
}

// Post-add behavior: fire the theme's own "product:added" event instead of
// navigating anywhere. Warehouse's header cart toggle listens for this event
// and opens its drawer, re-fetching cart contents itself - confirmed by
// watching a normal add-to-cart (2026-07-24): the theme's own listener needs
// no data beyond { quantity } in the event detail. Must dispatch from the
// enclosing <product-form> element, or (2026-09-23) document.documentElement
// as a fallback for callers with no product-form ancestor - the theme's
// cart-drawer controller delegates "product:added" on document.documentElement
// itself (assets/theme.js: `this.delegateRoot = new Delegate(document.documentElement)`),
// so a dispatch has to land there or bubble through it. Dispatching on
// `document` doesn't work: document has no parent to bubble through, so a
// listener bound to its child document.documentElement never sees it - this
// silently broke Product Add-Ons (2026-09-23), whose accessory picker block
// sits below the image gallery with no enclosing <product-form>, unlike the
// Grouped/Bundle wrapper's buybox swap which always had one. The add itself
// succeeded (confirmed via /cart.json) but nothing visible happened until an
// unrelated action - like using the product's own native Add to Cart -
// triggered a real drawer refresh that then picked up the already-added item.
document.addEventListener("hbi:added", (e) => {
  const target = e.target.closest?.("product-form") || document.documentElement;
  target.dispatchEvent(
    new CustomEvent("product:added", { bubbles: true, detail: { quantity: e.detail.quantity } })
  );
});
